import React, { useState, useMemo, useEffect } from "react";
import {
  UploadCloudIcon,
  EditIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  MenuIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LocationIcon,
  ServiceIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "../components/icons";
import {
  PROVIDERS,
  CATEGORIES,
  MAX_FILE_SIZE_BYTES,
  CATEGORY_PROVIDER_MAPPING,
  canMutateTenantData,
  CLIENT_ADMIN_PAGE,
  INVOICE_UPLOAD_PREFILL_KEY,
  INVOICE_UPLOAD_RETURN_KEY,
  INVOICE_UPLOAD_SUCCESS_TOAST_KEY,
} from "../config/constants";

import YmdDateInput from "../components/ui/YmdDateInput";
import InvoiceExtractionUpload from "../components/invoices/InvoiceExtractionUpload";
import InvoiceUploadMethodChooser from "../components/invoices/InvoiceUploadMethodChooser";
import {
  formatCurrency,
  formatTableDate,
  formatConcatenatedAddress,
  formatBandwidth,
  formatPhoneNumber,
  formatCircuitIdForDisplay,
  normalizeBillingAccount,
} from "../utils/helpers";
import { newGlAllocation, areGlAllocationsValid, serializeGlAllocations, formatGlAllocationsLabel } from "../utils/glCodes";
import InvoiceGlAllocationEditor from "../components/invoices/InvoiceGlAllocationEditor";
import {
  validateProviderCategory,
  validateFileProviderMatch,
  validateFileType,
  validateFileSize,
} from "../utils/validation";
import { useApi } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { useColumnMapping } from "../hooks/useColumnMapping";
import { useImportJobMetadata } from "../hooks/useImportJobMetadata";
import EditModal from "../components/modals/EditModal";
import Toast from "../components/ui/Toast";
import Spinner, { SpinnerCustom } from "../components/ui/Spinner";
import Loading from "../components/ui/Loading";
import { INVENTORY_FIELD_LABELS } from "../config/inventoryFields";
import { formatDateLocal } from "../utils/dateUtils";
import { isFutureInvoiceMonth, getCurrentInvoiceMonthEndDate, getCurrentInvoiceMonth, formatMonthLongYm, computeInvoiceProgressSummary } from "../utils/invoiceMonthDisplay";
import { getInventoryColumnLabel } from "../utils/columnLabelUtils";
import HeaderOptionsBar from "../components/layout/HeaderOptionsBar";
import MasterAddressPage from "./MasterAddressPage";
import ImportHistoryPanel from "../components/import/ImportHistoryPanel";
import ImportFieldMappingsPanel from "../components/import/ImportFieldMappingsPanel";
import ImportReplaceWizard, {
  ImportReplaceStepper,
} from "../components/import/ImportReplaceWizard";
import ReplaceComparisonGroupFilter from "../components/import/ReplaceComparisonGroupFilter";

import CustomSelect from "../components/ui/CustomSelect";
// Fallback field mapping when database mapping is unavailable
const FALLBACK_FIELD_MAPPING = {
  NIT: "circuit_id",
  "Service Number": "circuit_id",
  "Circuit ID": "circuit_id",
  "Bundle Type": "service_type",
  "Product Category": "service_type",
  "Component Name": "service_type",
  Status: "status",
  "Circuit Status Code": "status",
  "Street Address": "street_address",
  Address: "street_address",
  "A LOC Address": "street_address",
  City: "city",
  "A LOC City": "city",
  State: "state",
  "A LOC State": "state",
  ZIP: "zip",
  Zip: "zip",
  "A LOC Zip": "zip",
  Bandwidth: "bandwidth",
  "Bandwidth Capacity": "bandwidth",
  Provider: "last_mile_provider",
  "Provider Circuit ID": "provider_circuit_id",
  Term: "contract_term",
  "Contract Term (months)": "contract_term",
  "End of Term": "end_of_term",
  Expiration: "end_of_term",
  MRC: "mrc",
  "Total $": "mrc",
  "USD Amount": "mrc",
  "User First Name": "first_name",
  "User Last Name": "last_name",
  "User Email": "email",
  "Account Number": "billing_account",
  "Billing Account Number": "billing_account",
  "Works with circuit id": "works_with_circuit_id",
  Notes: "notes",
  "Follow Up Date": "follow_up_date",
  "Follow-up Date": "follow_up_date",
  "Followup Date": "follow_up_date",
};

// Valid import status values for filtering
const VALID_IMPORT_STATUSES = ["all", "new", "updated", "same"];

// Extract clean error message by removing prefixes
const extractErrorMessage = (errorMessage) => {
  if (!errorMessage) return "An error occurred";

  // Remove common prefixes
  const prefixes = [
    "Import API request failed: Error: ",
    "Import API request failed: ",
    "Error: ",
  ];

  let cleanMessage = errorMessage;
  for (const prefix of prefixes) {
    if (cleanMessage.startsWith(prefix)) {
      cleanMessage = cleanMessage.substring(prefix.length);
    }
  }

  return cleanMessage.trim();
};

const ImportPage = ({
  clientAdminRoute = CLIENT_ADMIN_PAGE.IMPORT,
  onNavigate,
  tenant,
  tenantId,
  inventory = [],
  providers = [],
  categories = [],
  statuses = [],
  serviceTypes = [],
  billingAccountsByProvider = [],
  glCodes = [],
  dropdownLoading = false,
  refreshDropdownValues,
  setHeaderOptionsContent,
  userRole,
}) => {
  const [activeSection, setActiveSection] = useState("upload");
  const [importStep, setImportStep] = useState("upload");
  /** history = import history home; wizard = replace-import flow */
  const [importView, setImportView] = useState("history");
  /** Replace-flow comparison from wizard preview (shown above legacy preview table) */
  const [replacePreviewComparison, setReplacePreviewComparison] = useState(null);
  const [replaceFileName, setReplaceFileName] = useState("");
  const [showReplaceConfirmModal, setShowReplaceConfirmModal] = useState(false);
  const [replaceConfirmAcknowledged, setReplaceConfirmAcknowledged] = useState(false);
  const [provider, setProvider] = useState("");
  const [category, setCategory] = useState("");
  // Upload Monthly Invoice form state
  const [invoiceProvider, setInvoiceProvider] = useState("");
  const [invoiceBillingAccount, setInvoiceBillingAccount] = useState("");
  const [invoiceGlCode, setInvoiceGlCode] = useState("");
  const [invoiceGlSplit, setInvoiceGlSplit] = useState(false);
  const [invoiceGlAllocations, setInvoiceGlAllocations] = useState(() => [
    newGlAllocation("", "100"),
  ]);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceMonthlyMrc, setInvoiceMonthlyMrc] = useState("");
  const [invoiceTotalAmount, setInvoiceTotalAmount] = useState("");
  const [invoiceCredit, setInvoiceCredit] = useState("");
  const [invoiceTaxesAndSurcharges, setInvoiceTaxesAndSurcharges] = useState("");
  const [invoiceDueDate, setInvoiceDueDate] = useState("");
  const [invoiceNote, setInvoiceNote] = useState("");
  const [invoicePdfFile, setInvoicePdfFile] = useState(null);
  const [invoicePdfFileName, setInvoicePdfFileName] = useState("");
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  /** Confirm modal before save + close-account on the manual upload form */
  const [invoicePendingSaveAndClose, setInvoicePendingSaveAndClose] =
    useState(false);
  const [closingAccountOnUpload, setClosingAccountOnUpload] = useState(false);
  /** choose = method picker; extract = multi-PDF Claude flow; manual = existing form */
  const [invoiceEntryMode, setInvoiceEntryMode] = useState("choose");
  const [invoiceMonthRows, setInvoiceMonthRows] = useState([]);
  const [closedMonths, setClosedMonths] = useState([]);
  // Use the actual tenant ID from authenticated user
  const selectedTenantId = tenantId;
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [groupedInventoryData, setGroupedInventoryData] = useState([]); // Store grouped data from API
  const [editingPreviewItem, setEditingPreviewItem] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [savedRecords, setSavedRecords] = useState(new Set());
  const [importResult, setImportResult] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isStandardizing, setIsStandardizing] = useState(false);
  const [apiColumns, setApiColumns] = useState(null);
  const [currentImportId, setCurrentImportId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "new", "updated", "same"
  const [expandedAddresses, setExpandedAddresses] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({
    sortBy: null,
    sortOrder: null,
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Show loader briefly when opening Client Administration (same as other navs)
  useEffect(() => {
    if (importStep !== "upload") return;
    const t = setTimeout(() => setIsInitialLoad(false), 50);
    return () => clearTimeout(t);
  }, [importStep]);

  const clientAdminSubView = useMemo(() => {
    if (clientAdminRoute === CLIENT_ADMIN_PAGE.INVOICE) return "invoice";
    return "import";
  }, [clientAdminRoute]);

  // Reset to import history when navigating back to Import inventory
  useEffect(() => {
    if (clientAdminRoute === CLIENT_ADMIN_PAGE.IMPORT) {
      setImportView("history");
    }
  }, [clientAdminRoute]);

  useEffect(() => {
    if (clientAdminRoute !== CLIENT_ADMIN_PAGE.INVOICE) return;
    try {
      const raw = sessionStorage.getItem(INVOICE_UPLOAD_PREFILL_KEY);
      if (!raw) return;
      sessionStorage.removeItem(INVOICE_UPLOAD_PREFILL_KEY);
      const prefill = JSON.parse(raw);
      if (prefill.provider) setInvoiceProvider(String(prefill.provider));
      if (prefill.billingAccount) {
        setInvoiceBillingAccount(String(prefill.billingAccount));
      }
      if (prefill.glCode) {
        setInvoiceGlCode(String(prefill.glCode));
        setInvoiceGlAllocations([newGlAllocation(String(prefill.glCode), "100")]);
        setInvoiceGlSplit(false);
      }
      if (prefill.invoiceMonth && /^\d{4}-\d{2}$/.test(prefill.invoiceMonth)) {
        setInvoiceDate(`${prefill.invoiceMonth}-01`);
      }
      // Prefill is used by "add missing invoice" — keep the existing manual form.
      setInvoiceEntryMode("manual");
    } catch {
      sessionStorage.removeItem(INVOICE_UPLOAD_PREFILL_KEY);
    }
  }, [clientAdminRoute]);

  const waitingInvoiceMonth = getCurrentInvoiceMonth();
  const waitingInvoiceRows = useMemo(
    () =>
      (invoiceMonthRows || []).filter(
        (r) =>
          // Closed accounts are not expected to send a new invoice.
          r.account_is_active !== false &&
          (r.row_kind === "missing" ||
            (r.row_kind === "new" && r.id == null)),
      ),
    [invoiceMonthRows],
  );
  const invoiceProgress = useMemo(
    () => computeInvoiceProgressSummary(invoiceMonthRows || []),
    [invoiceMonthRows],
  );
  const closedThroughLabel = useMemo(() => {
    const months = (closedMonths || [])
      .map(Number)
      .filter((m) => m >= 1 && m <= 12);
    if (!months.length) return "—";
    const last = Math.max(...months);
    const year = Number(String(waitingInvoiceMonth).slice(0, 4));
    return formatMonthLongYm(`${year}-${String(last).padStart(2, "0")}`) || "—";
  }, [closedMonths, waitingInvoiceMonth]);

  useEffect(() => {
    if (clientAdminSubView !== "invoice") return undefined;
    let cancelled = false;
    const year = String(waitingInvoiceMonth).slice(0, 4);
    apiService
      .listInvoices({ invoiceMonth: waitingInvoiceMonth })
      .then((res) => {
        if (cancelled) return;
        setInvoiceMonthRows(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setInvoiceMonthRows([]);
      });
    const params = new URLSearchParams({ year });
    if (tenantId != null && tenantId !== "") {
      params.set("tenant_id", String(tenantId));
    }
    apiService
      .request(`/analytics/closed-months?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setClosedMonths(res?.closed_months || []);
      })
      .catch(() => {
        if (!cancelled) setClosedMonths([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientAdminSubView, waitingInvoiceMonth, invoiceEntryMode, tenantId]);

  const [toast, setToast] = useState({ message: null, type: "success" });

  const {
    loading,
    importInventory,
    getImportRecordsData,
    getGroupedImportRecordsData,
    mergeImportJob,
    standardizeAllImportAddresses,
    updateImportRecord,
    getImportJobMetadata,
    getImportRecord,
    downloadCustomTemplate,
    executeReplaceFromJob,
  } = useApi();

  // Get import ID from localStorage for metadata hook
  const getStoredImportId = () => {
    return localStorage.getItem("lastImportId");
  };

  // Compute effective provider for hooks and API calls (must be defined before hooks that use it)
  const effectiveProviderForHooks = useMemo(() => {
    return category === "Custom" ? "Custom" : provider;
  }, [category, provider]);

  // Use metadata hook when import ID is available (for viewing import jobs)
  const importIdForMetadata = currentImportId || getStoredImportId();
  const {
    fieldMappingLookup: metadataFieldMappingLookup,
    columns: metadataColumns,
  } = useImportJobMetadata(
    importIdForMetadata,
    effectiveProviderForHooks,
    category,
    selectedTenantId,
  );

  // Get dynamic column mapping from database (fallback for upload/other scenarios)
  const {
    fieldMappingLookup: columnMappingFieldMappingLookup,
    getInputColumns,
  } = useColumnMapping(effectiveProviderForHooks, category, tenantId);

  // Prefer metadata fieldMappingLookup if available, fallback to column mapping
  const fieldMappingLookup =
    metadataFieldMappingLookup &&
    Object.keys(metadataFieldMappingLookup).length > 0
      ? metadataFieldMappingLookup
      : columnMappingFieldMappingLookup;

  // Get dynamic columns from API response, metadata, or column mapping
  // Prefer: API response columns > metadata columns > column mapping
  const rawDynamicColumns = useMemo(() => {
    // Use API columns directly if available (from data endpoint)
    if (apiColumns && apiColumns.length > 0) {
      return apiColumns;
    }
    // Fallback to metadata columns if available
    if (metadataColumns && metadataColumns.length > 0) {
      return metadataColumns;
    }
    // Last fallback to column mapping
    const inputColumns = getInputColumns();
    return inputColumns;
  }, [apiColumns, metadataColumns, getInputColumns]);

  // Ensure previewData is always an array (defined early for use in dynamicColumns)
  const safePreviewData = useMemo(() => {
    return Array.isArray(previewData) ? previewData : [];
  }, [previewData]);

  // Filter dynamic columns to exclude individual address columns and add "address" if needed
  // Also exclude Provider, Category, and Circuit ID fields since they're shown as fixed columns
  // Provider and Category are always shown as fixed columns (from file for Custom, from dropdown for others)
  const dynamicColumns = useMemo(() => {
    if (!rawDynamicColumns || rawDynamicColumns.length === 0) {
      return rawDynamicColumns;
    }

    const individualAddressFields = [
      "std_street_address",
      "std_city",
      "std_state",
      "std_zip",
      "std_country",
      "street_address",
      "city",
      "state",
      "zip",
      "country",
      // Also check for common variations from API
      "Street Address",
      "A LOC Address",
      "City",
      "A LOC City",
      "State",
      "A LOC State",
      "ZIP",
      "Zip",
      "A LOC Zip",
      "Country",
    ];

    // Helper function to check if a column is an address field (case-insensitive)
    const isAddressField = (col) => {
      const colLower = col.toLowerCase().trim();
      return (
        individualAddressFields.some(
          (field) => field.toLowerCase() === colLower,
        ) ||
        colLower.includes("street") ||
        (colLower.includes("address") && !colLower.includes("billing")) ||
        colLower === "city" ||
        colLower === "state" ||
        colLower === "zip" ||
        colLower === "country" ||
        colLower === "std_city" ||
        colLower === "std_state" ||
        colLower === "std_zip" ||
        colLower === "std_country" ||
        colLower === "std_street_address"
      );
    };

    // Helper function to check if a column is Provider or Category (case-insensitive)
    const isProviderOrCategoryField = (col) => {
      const colLower = col.toLowerCase().trim();
      return colLower === "provider" || colLower === "category";
    };

    // Helper function to check if a column is an MRC-related field
    const isMRCField = (col) => {
      if (!col) return false;
      const colLower = col.toLowerCase().trim();
      const colTrimmed = col.trim();
      return (
        colLower === "mrc" ||
        col === "MRC" ||
        colTrimmed === "Total $" ||
        colTrimmed === "Total$" ||
        colLower === "total $" ||
        colLower === "total$" ||
        col === "USD Amount" ||
        colLower === "usd amount" ||
        (fieldMappingLookup && fieldMappingLookup[col] === "mrc")
      );
    };

    // Check if we have address components in the data
    const hasAddressComponents = safePreviewData.some(
      (record) =>
        record.std_street_address ||
        record.std_city ||
        record.std_state ||
        record.std_zip ||
        record.std_country ||
        record.street_address ||
        record.city ||
        record.state ||
        record.zip ||
        record.country ||
        record["Street Address"] ||
        record["A LOC Address"] ||
        record["City"] ||
        record["A LOC City"] ||
        record["State"] ||
        record["A LOC State"] ||
        record["ZIP"] ||
        record["Zip"] ||
        record["A LOC Zip"] ||
        record["Country"],
    );

    // Filter out individual address columns
    // For Custom category: Exclude Provider and Category from dynamic columns (shown as fixed columns at front)
    // For other categories: Exclude Provider and Category (shown as fixed columns from dropdown)
    let filteredColumns = rawDynamicColumns.filter((col) => {
      // Always filter out address fields
      if (isAddressField(col)) {
        return false;
      }
      // Always filter out provider and category (shown as fixed columns for both Custom and non-Custom)
      if (isProviderOrCategoryField(col)) {
        return false;
      }
      // Always filter out MRC/Total $ fields (shown separately on right for all providers)
      if (isMRCField(col)) {
        return false;
      }
      return true;
    });

    // Add "address" column if address components exist and it's not already present
    if (hasAddressComponents && !filteredColumns.includes("address")) {
      // Insert "address" in a logical position (after billing_account if exists, otherwise at end)
      const billingIndex = filteredColumns.findIndex((col) =>
        col.toLowerCase().includes("billing"),
      );
      if (billingIndex !== -1) {
        filteredColumns.splice(billingIndex + 1, 0, "address");
      } else {
        filteredColumns.push("address");
      }
    }

    return filteredColumns;
  }, [
    rawDynamicColumns,
    safePreviewData,
    category,
    provider,
    fieldMappingLookup,
  ]); // Add provider and fieldMappingLookup to dependencies

  // Order columns with priority: Circuit ID, Address, Bandwidth, MRC, Term, End of Term, then others
  const orderedColumns = useMemo(() => {
    if (!dynamicColumns || dynamicColumns.length === 0) {
      return dynamicColumns;
    }

    // Find circuit_id field using fieldMappingLookup (could be "NIT", "Circuit ID", etc.)
    let circuitIdColumn = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      circuitIdColumn = Object.keys(fieldMappingLookup).find(
        (inputCol) => fieldMappingLookup[inputCol] === "circuit_id",
      );
    }
    if (!circuitIdColumn && dynamicColumns.includes("circuit_id")) {
      circuitIdColumn = "circuit_id";
    }
    if (!circuitIdColumn) {
      const circuitVariations = [
        "NIT",
        "Circuit ID",
        "Service Number",
        "circuit_id",
      ];
      circuitIdColumn = circuitVariations.find((col) =>
        dynamicColumns.includes(col),
      );
    }

    // Find bandwidth column
    let bandwidthColumn = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      bandwidthColumn = Object.keys(fieldMappingLookup).find(
        (inputCol) => fieldMappingLookup[inputCol] === "bandwidth",
      );
    }
    if (!bandwidthColumn && dynamicColumns.includes("bandwidth")) {
      bandwidthColumn = "bandwidth";
    }
    if (!bandwidthColumn) {
      const bandwidthVariations = ["Bandwidth", "Bandwidth Capacity"];
      bandwidthColumn = bandwidthVariations.find((col) =>
        dynamicColumns.includes(col),
      );
    }

    // Find MRC column
    let mrcColumn = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      mrcColumn = Object.keys(fieldMappingLookup).find(
        (inputCol) => fieldMappingLookup[inputCol] === "mrc",
      );
    }
    if (!mrcColumn && dynamicColumns.includes("mrc")) {
      mrcColumn = "mrc";
    }
    if (!mrcColumn) {
      const mrcVariations = ["MRC", "Total $", "USD Amount"];
      mrcColumn = mrcVariations.find((col) => dynamicColumns.includes(col));
    }

    // Find Term column (contract_term)
    let termColumn = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      termColumn = Object.keys(fieldMappingLookup).find(
        (inputCol) => fieldMappingLookup[inputCol] === "contract_term",
      );
    }
    if (!termColumn && dynamicColumns.includes("contract_term")) {
      termColumn = "contract_term";
    }
    if (!termColumn) {
      const termVariations = ["Term", "Contract Term (months)"];
      termColumn = termVariations.find((col) => dynamicColumns.includes(col));
    }

    // Find End of Term column
    let endOfTermColumn = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      endOfTermColumn = Object.keys(fieldMappingLookup).find(
        (inputCol) => fieldMappingLookup[inputCol] === "end_of_term",
      );
    }
    if (!endOfTermColumn && dynamicColumns.includes("end_of_term")) {
      endOfTermColumn = "end_of_term";
    }
    if (!endOfTermColumn) {
      const endOfTermVariations = ["End of Term", "Expiration"];
      endOfTermColumn = endOfTermVariations.find((col) =>
        dynamicColumns.includes(col),
      );
    }

    // Build ordered columns in the required sequence
    const priorityColumns = [];

    // 1. Circuit ID (NIT/Circuit ID/Service Number)
    if (circuitIdColumn) {
      priorityColumns.push(circuitIdColumn);
    }

    // 2. Address
    if (dynamicColumns.includes("address")) {
      priorityColumns.push("address");
    }

    // 3. Bandwidth
    if (bandwidthColumn) {
      priorityColumns.push(bandwidthColumn);
    }

    // 4. MRC (always skipped - shown separately on right for all providers)
    // MRC is excluded from priority columns since it's always displayed in the fixed right column

    // 5. Term
    if (termColumn) {
      priorityColumns.push(termColumn);
    }

    // 6. End of Term
    if (endOfTermColumn) {
      priorityColumns.push(endOfTermColumn);
    }

    // Add other columns in original order (excluding already added priority columns)
    const otherColumns = dynamicColumns.filter(
      (col) => !priorityColumns.includes(col),
    );

    return [...priorityColumns, ...otherColumns];
  }, [dynamicColumns, fieldMappingLookup, provider, category]);

  // Get field mapping from database mapping
  const getFieldMappingFromAPI = () => {
    // Create mapping in format: { "NIT": ["circuit_id"], ... }
    const mapping = {};

    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      Object.entries(fieldMappingLookup).forEach(
        ([inputColumn, inventoryColumn]) => {
          mapping[inputColumn] = [inventoryColumn];
        },
      );
    }

    // If no mapping from database, return empty object (will fall back to direct column names)
    return mapping;
  };

  // Compute available providers based on selected category
  const availableProviders = useMemo(() => {
    if (!category) return [];
    if (category === "Custom") return ["Custom"];
    return CATEGORY_PROVIDER_MAPPING[category] || [];
  }, [category]);

  // Reset provider when category changes
  useEffect(() => {
    if (category === "Custom") {
      // Automatically set provider to "Custom" when Custom category is selected
      setProvider("Custom");
    } else if (category && category !== "Custom") {
      // If there's only one available provider for this category, auto-select it
      if (availableProviders.length === 1) {
        setProvider(availableProviders[0]);
      } else if (availableProviders.length > 1) {
        // If multiple providers available, reset if current provider is not in the list
        if (provider && !availableProviders.includes(provider)) {
          setProvider("");
        }
      } else {
        // No providers available, clear selection
        setProvider("");
      }
    } else if (!category) {
      // Clear provider when category is cleared
      setProvider("");
    }
  }, [category, availableProviders, provider]);

  // Validate provider-category combination and file-provider match in real-time
  useEffect(() => {
    let error = null;

    // Determine effective provider
    const effectiveProvider = category === "Custom" ? "Custom" : provider;

    // Validate provider-category combination if both are selected (skip for "Custom")
    if (effectiveProvider && category && category !== "Custom") {
      const providerCategoryValidation = validateProviderCategory(
        effectiveProvider,
        category,
      );
      if (!providerCategoryValidation.isValid) {
        error = providerCategoryValidation.errorMessage;
      }
    }

    // Validate file-provider match if provider and file are selected (skip for "Custom")
    if (!error && effectiveProvider && selectedFile && category !== "Custom") {
      const fileProviderValidation = validateFileProviderMatch(
        effectiveProvider,
        selectedFile.name,
      );
      if (!fileProviderValidation.isMatch) {
        error = fileProviderValidation.errorMessage;
      }
    }

    setValidationError(error);
  }, [provider, category, selectedFile]);

  // ============================================
  // Reload data when status filter or sort changes
  // ============================================
  useEffect(() => {
    // Reload staged address table when filters change (Import step / legacy preview).
    // Skip replace comparison-only preview — that screen has no address table.
    const showAddressReview =
      importStep === "import" ||
      (importStep === "preview" && !replacePreviewComparison);
    if (showAddressReview && currentImportId) {
      // Reset to page 1 when filter or sort changes (user expects to see first page of filtered/sorted results)
      const importId = currentImportId || localStorage.getItem("lastImportId");
      if (importId) {
        loadPage(importId, 1, pageSize).catch((error) => {
          console.error(
            "Failed to reload data after filter/sort change:",
            error,
          );
          // Don't show error to user - just log it
          // The filter/sort change will be applied on next manual page load
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: We intentionally exclude loadPage from dependencies to avoid infinite loops
    // loadPage is stable and doesn't need to be in deps
  }, [
    statusFilter,
    sortConfig.sortBy,
    sortConfig.sortOrder,
    importStep,
    replacePreviewComparison,
    currentImportId,
  ]); // Reload when filter or sort changes

  // Set header options content for Client Administration
  useEffect(() => {
    if (!setHeaderOptionsContent) return;
    if (
      (clientAdminSubView === "import" &&
        (importView === "history" ||
          importView === "mappings" ||
          importView === "wizard" ||
          importView === "review")) ||
      clientAdminSubView === "invoice"
    ) {
      setHeaderOptionsContent(null);
      return () => setHeaderOptionsContent(null);
    }
    setHeaderOptionsContent(
      <HeaderOptionsBar
        mode="client-admin"
        activeTab={activeSection}
        onTabChange={setActiveSection}
        onNavigate={onNavigate}
      />,
    );
    return () => {
      setHeaderOptionsContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderOptionsContent, activeSection, importView, clientAdminSubView]);

  const handleImportAndPreview = async () => {
    // Clear previous errors
    setApiError(null);
    setValidationError(null);

    // Determine the effective provider (use "Custom" for "Custom" category)
    const effectiveProvider = category === "Custom" ? "Custom" : provider;

    // Basic required fields check
    if (!category || !selectedFile) {
      setApiError("Please select a category and a file.");
      return;
    }

    // For Custom category, provider is automatically "Custom"
    // For other categories, require provider selection
    if (category !== "Custom" && !provider) {
      setApiError("Please select a provider.");
      return;
    }

    // Validate provider-category combination (skip for "Custom" category)
    if (category !== "Custom") {
      const providerCategoryValidation = validateProviderCategory(
        effectiveProvider,
        category,
      );
      if (!providerCategoryValidation.isValid) {
        setValidationError(providerCategoryValidation.errorMessage);
        return;
      }
    }

    // Validate file type
    const fileTypeValidation = validateFileType(selectedFile);
    if (!fileTypeValidation.isValid) {
      setApiError(fileTypeValidation.errorMessage);
      return;
    }

    // Validate file size
    const fileSizeValidation = validateFileSize(
      selectedFile,
      MAX_FILE_SIZE_BYTES,
    );
    if (!fileSizeValidation.isValid) {
      setApiError(fileSizeValidation.errorMessage);
      return;
    }

    // Validate file-provider match (skip for "Custom" category to allow generic files)
    if (category !== "Custom") {
      const fileProviderValidation = validateFileProviderMatch(
        effectiveProvider,
        selectedFile.name,
      );
      if (!fileProviderValidation.isMatch) {
        setValidationError(fileProviderValidation.errorMessage);
        return;
      }
    }

    setIsUploading(true);
    setShowLoader(true);
    setApiError(null);
    setValidationError(null);

    // Show loader for minimum 2 seconds
    const loaderPromise = new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      // Create FormData for API
      const formData = new FormData();
      formData.append("provider", effectiveProvider);
      formData.append("category", category);
      formData.append("tenant_id", selectedTenantId);
      formData.append("file", selectedFile);

      // Debug: Log form data contents
      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      // Call API
      const result = await importInventory(formData);
      setImportResult(result);
      setIsSuccess(true);
      setApiError(null);

      // Store import ID, provider, and category in localStorage and state for later use
      if (result.data && result.data.import_id) {
        const importId = result.data.import_id;
        localStorage.setItem("lastImportId", importId);
        localStorage.setItem("lastImportProvider", effectiveProvider);
        localStorage.setItem("lastImportCategory", category);
        setCurrentImportId(importId.toString());
      }

      // Wait for minimum loader time
      await loaderPromise;
      setShowLoader(false);

      // Automatically load preview with summary shown at top
      try {
        setIsLoadingPreview(true);
        setCurrentPage(1);

        // Load first page of import records
        if (result.data && result.data.import_id) {
          const importId = result.data.import_id.toString();
          await loadPage(importId, 1, pageSize);
        }

        // Go directly to preview step (summary is shown at top)
        setImportStep("preview");
        setIsLoadingPreview(false);
      } catch (previewError) {
        console.error("Failed to load preview:", previewError);
        // If preview fails, still go to summary step as fallback
        setImportStep("summary");
        setIsLoadingPreview(false);
      }
    } catch (error) {
      console.error("Import error:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        provider,
        category,
        tenantId: selectedTenantId,
        fileName: selectedFile?.name,
        fileSize: selectedFile?.size,
        fileType: selectedFile?.type,
        errorDetails: error.details,
      });

      // apiService already sets error.message from backend response
      // Just use it directly - it contains the detailed user-friendly message
      let errorMessage = error.message || "An error occurred";

      // Handle network errors with specific message
      if (error.isNetworkError || error.message === "Unable to load the data") {
        errorMessage = "Unable to load the data";
      }
      // Handle file size errors
      else if (error.message && error.message.includes("413")) {
        errorMessage = "File too large. Please try with a smaller file.";
      }
      // For all other errors, error.message already contains the backend's formatted message
      // This includes: mapping validation, no valid records, server errors, client errors, etc.

      setApiError(extractErrorMessage(errorMessage));
      setIsSuccess(false);

      // Wait for minimum loader time even on error
      await loaderPromise;
      setShowLoader(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreviewClick = async () => {
    try {
      setIsLoadingPreview(true);
      setCurrentPage(1);

      // Get import ID, provider, and category from localStorage
      const importId = localStorage.getItem("lastImportId");
      const storedProvider = localStorage.getItem("lastImportProvider");
      const storedCategory = localStorage.getItem("lastImportCategory");

      if (!importId) {
        console.error("No import ID found in localStorage");
        setApiError("No import data found. Please import a file first.");
        return;
      }

      // Use stored provider/category if state is missing
      const effectiveProvider = provider || storedProvider || "";
      const effectiveCategory = category || storedCategory || "";

      if (!effectiveProvider || !effectiveCategory) {
        setApiError(
          "Provider and category are required. Please select them and try again.",
        );
        return;
      }

      // Ensure state is set for loadPage (if not already set)
      if (!provider && storedProvider) {
        setProvider(storedProvider);
      }
      if (!category && storedCategory) {
        setCategory(storedCategory);
      }

      // Load first page
      await loadPage(importId, 1, pageSize);

      // Go to preview step
      setImportStep("preview");
    } catch (error) {
      console.error("Failed to fetch import records:", error);

      // Handle specific "fn is not a function" error
      if (
        error.message &&
        (error.message.includes("fn is not a function") ||
          error.message.includes("is not a function"))
      ) {
        setApiError(
          "Server configuration error. Please contact support or try refreshing the page.",
        );
        console.error("Function call error - possible backend issue:", {
          error: error.message,
          stack: error.stack,
          details: error.details,
        });
        return;
      }

      // Show more specific error messages
      if (error.isNetworkError || error.message === "Unable to load the data") {
        setApiError("Unable to load the data");
      } else if (error.details) {
        // Show backend error message if available
        const backendMessage =
          error.details.message || error.details.error || error.message;

        // Check if backend message contains function error
        if (
          backendMessage &&
          (backendMessage.includes("fn is not a function") ||
            backendMessage.includes("is not a function"))
        ) {
          setApiError(
            "Server configuration error. Please contact support or try refreshing the page.",
          );
        } else {
          setApiError(`Failed to load preview data: ${backendMessage}`);
        }
      } else {
        // Check if error message contains function error
        if (
          error.message &&
          (error.message.includes("fn is not a function") ||
            error.message.includes("is not a function"))
        ) {
          setApiError(
            "Server configuration error. Please contact support or try refreshing the page.",
          );
        } else {
          setApiError(
            `Failed to load preview data: ${
              error.message || "Please try again."
            }`,
          );
        }
      }
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const loadPage = async (
    importId,
    page,
    pageSize = 20,
    overrideSortBy = null,
    overrideSortOrder = null,
  ) => {
    try {
      setIsLoadingPage(true);

      // Use importId parameter if provided, fall back to localStorage
      const importJobId = importId || localStorage.getItem("lastImportId");
      if (!importJobId) {
        throw new Error("No import ID found. Please import a file first.");
      }

      // Update current import ID state for metadata hook
      setCurrentImportId(importJobId.toString());

      // Get stored provider/category as fallback
      const storedProvider = localStorage.getItem("lastImportProvider");
      const storedCategory = localStorage.getItem("lastImportCategory");

      // Use state values, fallback to stored values
      const currentCategory = category || storedCategory || "";
      const currentProvider = provider || storedProvider || "";
      const effectiveProviderForLoad =
        currentCategory === "Custom" ? "Custom" : currentProvider;

      if (!effectiveProviderForLoad || !currentCategory) {
        throw new Error(
          "Provider and category are required to load import records. Please select them and try again.",
        );
      }

      // Use override sort values if provided, otherwise use state
      const effectiveSortBy =
        overrideSortBy !== null ? overrideSortBy : sortConfig.sortBy;
      const effectiveSortOrder =
        overrideSortOrder !== null
          ? overrideSortOrder
          : sortConfig.sortOrder || "asc";

      // Validate and normalize statusFilter
      const normalizedStatusFilter = VALID_IMPORT_STATUSES.includes(
        statusFilter,
      )
        ? statusFilter
        : "all";

      // Convert 'all' to null for API (backend expects null for "show all")
      const effectiveStatusFilter =
        normalizedStatusFilter !== "all" ? normalizedStatusFilter : null;

      console.log(
        "Loading page with import ID:",
        importJobId,
        "page:",
        page,
        "provider:",
        effectiveProviderForLoad,
        "category:",
        currentCategory,
        "statusFilter:",
        statusFilter,
        "effectiveStatusFilter:",
        effectiveStatusFilter,
        "sortBy:",
        effectiveSortBy,
        "sortOrder:",
        effectiveSortOrder,
      );

      // Use getGroupedImportRecordsData endpoint with provider, category, tenant_id, status filter, and sorting
      // Pass statusFilter to API (convert 'all' to null for API)
      // Pass sortBy and sortOrder for backend-driven sorting
      const response = await getGroupedImportRecordsData(
        importJobId,
        effectiveProviderForLoad,
        currentCategory,
        selectedTenantId,
        page,
        pageSize,
        effectiveStatusFilter,
        effectiveSortBy,
        effectiveSortOrder,
      );

      // API response structure: { success, data: [addressGroups], columns: [...], pagination: {...} }
      // Each addressGroup: { addressKey, std_* fields, items: [records], totalMRC, totalCount }
      const columnsFromAPI = response.columns || [];
      const addressGroups = response.data || [];
      const paginationInfo = response.pagination;

      console.log("API response (grouped):", {
        columnsCount: columnsFromAPI.length,
        addressGroupsCount: addressGroups.length,
        hasPagination: !!paginationInfo,
        statusFilter: effectiveStatusFilter || "all",
        totalRecords: paginationInfo?.total || 0,
      });

      // Flatten address groups to get all records for previewData (for backward compatibility with edit functionality)
      // But we'll primarily use the grouped structure for display
      const allRecords = [];
      addressGroups.forEach((group) => {
        if (group.items && Array.isArray(group.items)) {
          allRecords.push(...group.items);
        }
      });

      // Validate we have data to display
      if (
        addressGroups.length === 0 &&
        (!paginationInfo || paginationInfo.total === 0)
      ) {
        console.warn("No address groups found in API response", {
          statusFilter: effectiveStatusFilter || "all",
          page,
        });
        setPreviewData([]);
        setApiColumns(columnsFromAPI);
        setTotalPages(1);
        setTotalRecords(0);
        setCurrentPage(page);
        // Set empty grouped data to ensure empty state displays properly
        setGroupedInventoryData([]);
        // Don't show error for filtered results - it's expected if no records match the filter
        if (effectiveStatusFilter) {
          console.log(
            `No records found matching status filter: ${effectiveStatusFilter}`,
          );
        }
        return;
      }

      // Set columns from API response
      setApiColumns(columnsFromAPI);
      console.log("Columns set:", columnsFromAPI.length, "columns");

      // Update pagination if provided (pagination is at address group level)
      if (paginationInfo) {
        setTotalPages(paginationInfo.totalPages || 1);
        setTotalRecords(paginationInfo.total || addressGroups.length);
        console.log("Pagination (grouped):", {
          currentPage: page,
          totalPages: paginationInfo.totalPages || 1,
          total: paginationInfo.total || addressGroups.length,
        });
      } else {
        setTotalPages(1);
        setTotalRecords(addressGroups.length);
        console.log(
          "No pagination info, defaulting to single page with",
          addressGroups.length,
          "address groups",
        );
      }

      // Store flattened records for backward compatibility (edit functionality needs individual records)
      setPreviewData(allRecords);

      // Store grouped data from API (with formatted address field added).
      // Fall back to raw input fields when std_* is empty (needs_master / unlinked).
      const groupedWithAddress = addressGroups.map((group) => {
        const first = group.items && group.items[0] ? group.items[0] : null;
        const formatted = formatConcatenatedAddress({
          std_street_address:
            group.std_street_address || first?.street_address || "",
          std_city: group.std_city || first?.city || "",
          std_state: group.std_state || first?.state || "",
          std_zip: group.std_zip || first?.zip || "",
          std_country: group.std_country || first?.country || "",
        });
        return {
          ...group,
          address: formatted,
        };
      });
      setGroupedInventoryData(groupedWithAddress);
      setCurrentPage(page);

      // Debug: Log verified field for first record to verify it's being returned
      if (allRecords.length > 0 && process.env.NODE_ENV !== "production") {
        console.log("=== PAGE LOADED - VERIFIED FIELD CHECK ===");
        console.log("First record verified value:", allRecords[0].verified);
        console.log(
          "First record verified type:",
          typeof allRecords[0].verified,
        );
        console.log("isVerified result:", isVerified(allRecords[0].verified));
        console.log("==========================================");
      }

      console.log(
        "Successfully loaded page",
        page,
        "with",
        allRecords.length,
        "records",
      );
    } catch (error) {
      console.error(`Failed to load page ${page}:`, error);

      // Get importId for error logging (use parameter or fallback to localStorage)
      const errorImportId =
        importId || localStorage.getItem("lastImportId") || "unknown";

      // Handle specific "fn is not a function" error
      if (
        error.message &&
        (error.message.includes("fn is not a function") ||
          error.message.includes("is not a function"))
      ) {
        const errorMessage =
          "Server configuration error. Please contact support or try refreshing the page.";
        setApiError(errorMessage);
        console.error("Function call error - possible backend issue:", {
          error: error.message,
          stack: error.stack,
          details: error.details,
          page,
          importId: errorImportId,
        });
        throw new Error(errorMessage);
      }

      // Show more specific error messages
      if (error.isNetworkError || error.message === "Unable to load the data") {
        setApiError("Unable to load the data");
      } else if (error.details) {
        // Show backend error message if available
        const backendMessage =
          error.details.message || error.details.error || error.message;

        // Check if backend message contains function error
        if (
          backendMessage &&
          (backendMessage.includes("fn is not a function") ||
            backendMessage.includes("is not a function"))
        ) {
          const errorMessage =
            "Server configuration error. Please contact support or try refreshing the page.";
          setApiError(errorMessage);
          throw new Error(errorMessage);
        } else {
          setApiError(`Failed to load page ${page}: ${backendMessage}`);
        }
      } else {
        // Check if error message contains function error
        if (
          error.message &&
          (error.message.includes("fn is not a function") ||
            error.message.includes("is not a function"))
        ) {
          const errorMessage =
            "Server configuration error. Please contact support or try refreshing the page.";
          setApiError(errorMessage);
          throw new Error(errorMessage);
        } else {
          setApiError(
            `Failed to load page ${page}: ${
              error.message || "Please try again."
            }`,
          );
        }
      }
      throw error;
    } finally {
      setIsLoadingPage(false);
    }
  };

  const handlePageChange = async (page) => {
    const importId = localStorage.getItem("lastImportId");
    if (importId && page >= 1 && page <= totalPages) {
      await loadPage(importId, page, pageSize);
    }
  };

  const handlePageSizeChange = async (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when page size changes
    const importId = localStorage.getItem("lastImportId");
    if (importId) {
      await loadPage(importId, 1, newPageSize);
    }
  };

  /**
   * Export Changes JSON functionality - DISABLED
   *
   * This function was used to export tracked changes as a JSON file.
   * Currently hidden from UI and disabled.
   *
   * To re-enable:
   * 1. Uncomment this function
   * 2. Uncomment the Export Changes JSON button in the preview section
   */
  // const exportChanges = () => {
  //   const changes = getAllChanges();
  //   const exportData = {
  //     importId: localStorage.getItem("lastImportId"),
  //     exportDate: new Date().toISOString(),
  //     totalChanges: changes.length,
  //     changes: changes,
  //   };
  //
  //   const dataStr = JSON.stringify(exportData, null, 2);
  //   const dataBlob = new Blob([dataStr], { type: "application/json" });
  //   const url = URL.createObjectURL(dataBlob);
  //   const link = document.createElement("a");
  //   link.href = url;
  //   link.download = `import-changes-${
  //     new Date().toISOString().split("T")[0]
  //   }.json`;
  //   link.click();
  //   URL.revokeObjectURL(url);
  // };

  const openReplaceConfirmModal = () => {
    setReplaceConfirmAcknowledged(false);
    setShowReplaceConfirmModal(true);
  };

  const closeReplaceConfirmModal = () => {
    if (isMerging) return;
    setShowReplaceConfirmModal(false);
    setReplaceConfirmAcknowledged(false);
  };

  const handleConfirmMerge = async () => {
    try {
      setIsMerging(true);
      setApiError(null);

      // Get import ID from localStorage
      const importId = localStorage.getItem("lastImportId");

      if (!importId) {
        const missingIdMsg =
          "No import ID found. Please import a file first.";
        setApiError(missingIdMsg);
        setToast({ message: missingIdMsg, type: "error" });
        return;
      }

      if (replacePreviewComparison) {
        const effectiveProvider = category === "Custom" ? "Custom" : provider;
        await executeReplaceFromJob({
          import_id: Number(importId),
          provider: effectiveProvider,
          category,
          new_count: replacePreviewComparison.counts?.added ?? 0,
          updated_count: replacePreviewComparison.counts?.changed ?? 0,
          left_out_count: replacePreviewComparison.counts?.leaving ?? 0,
          unchanged_count: replacePreviewComparison.counts?.unchanged ?? 0,
        });
        localStorage.setItem("lastImportId", importId);
        setShowReplaceConfirmModal(false);
        setReplaceConfirmAcknowledged(false);
        setImportStep("complete");
        return;
      }

      // Call merge API (legacy non-replace path)
      await mergeImportJob(importId);

      // Store import ID for inventory refresh
      localStorage.setItem("lastImportId", importId);

      // Go to complete step
      setImportStep("complete");
    } catch (error) {
      console.error("Merge failed:", error);
      let errorMessage;
      let toastType = "error";
      let toastTitle;

      if (error.isNetworkError || error.message === "Unable to load the data") {
        errorMessage = "Unable to load the data";
      } else if (error.status === 409) {
        errorMessage =
          "Another inventory merge is already in progress. Refresh the page and try again in a few moments.";
        toastType = "warning";
        toastTitle = replacePreviewComparison
          ? "Replace In Progress"
          : "Merge In Progress";
      } else if (error.status === 400 || error.status === 500) {
        errorMessage =
          error.message ||
          "Unable to process your request right now. Please try again later.";
      } else {
        errorMessage =
          error.message ||
          (replacePreviewComparison
            ? "Replace failed. Please try again."
            : "Merge failed. Please try again.");
      }

      setApiError(errorMessage);
      setToast({
        title: toastTitle,
        message: errorMessage,
        type: toastType,
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleStandardizeAllAddresses = async () => {
    try {
      setIsStandardizing(true);
      setApiError(null);

      // Get import ID from localStorage or currentImportId
      const importId = currentImportId || localStorage.getItem("lastImportId");

      if (!importId) {
        setApiError("No import ID found. Please import a file first.");
        return;
      }

      // Call standardization API
      const response = await standardizeAllImportAddresses(importId);

      // Show success message
      if (response && response.summary) {
        const { total, successful, failed } = response.summary;
        if (successful > 0) {
          // Refresh the data to show updated addresses
          await loadPage(importId, currentPage, pageSize);
          setApiError(null);
          // Success toast intentionally not shown for "Standardize all addresses"
        } else {
          const msg =
            response.message ||
            "No addresses were standardized. Please check the import records.";
          setApiError(msg);
          setToast({ message: msg, type: "error" });
        }
      } else {
        // Refresh data even if response format is unexpected
        await loadPage(importId, currentPage, pageSize);
        // Success toast intentionally not shown for "Standardize all addresses"
      }
    } catch (error) {
      console.error("Standardization failed:", error);
      const errorMsg =
        error.isNetworkError || error.message === "Unable to load the data"
          ? "Unable to load the data"
          : error.status === 400 || error.status === 500
            ? "Unable to process your request right now. Please try again later."
            : error.message ||
              "Address standardization failed. Please try again.";
      setApiError(errorMsg);
      setToast({ message: errorMsg, type: "error" });
    } finally {
      setIsStandardizing(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileName(file.name);
      // Validation will be handled by useEffect hook
    } else {
      setSelectedFile(null);
      setFileName("");
    }
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();
    try {
      console.log("Downloading Custom template for tenant:", selectedTenantId);
      await downloadCustomTemplate(selectedTenantId);
      // Clear any previous errors on success
      setApiError(null);
      console.log("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", {
        error,
        message: error.message,
        status: error.status,
        tenantId: selectedTenantId,
        isNetworkError: error.isNetworkError,
      });

      let errorMessage = "Failed to download template.";

      // Provide specific error messages based on error type
      if (error.isNetworkError) {
        errorMessage =
          "Network error: Unable to download template. Please check your connection and try again.";
      } else if (error.status === 404) {
        // Use backend error message if available, otherwise use default
        errorMessage =
          error.message ||
          "Template not found. Please ensure Custom mappings are configured (either tenant-specific or shared defaults).";
      } else if (error.status === 500) {
        errorMessage =
          error.message ||
          "Server error while downloading template. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage =
          "Failed to download template. Please ensure Custom mappings are configured for this tenant.";
      }

      setApiError(errorMessage);
      alert(`⚠️ ${errorMessage}`);
    }
  };

  const resetImport = () => {
    setProvider("");
    setCategory("");
    setSelectedFile(null);
    setFileName("");
    setPreviewData([]);
    setImportStep("upload");
    setImportView("history");
    setReplacePreviewComparison(null);
    setReplaceFileName("");
    setApiError(null);
    setValidationError(null);
    setImportResult(null);
    setIsSuccess(false);
    setIsLoadingPreview(false);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalRecords(0);
    setIsLoadingPage(false);
    setEditingPreviewItem(null);
    setIsMerging(false);
    setSavedRecords(new Set());
    setStatusFilter("all"); // Reset status filter to "all"
    setSortConfig({ sortBy: null, sortOrder: null }); // Reset sort config
    setGroupedInventoryData([]); // Clear grouped data
    setApiColumns(null); // Clear API columns
    setCurrentImportId(null);
  };

  // ============================================
  // Helper Function for Transformation
  // Maps input column names to database column names using metadata fieldMappingLookup
  // Only includes fields that have changed compared to originalItem
  // ============================================
  /**
   * Prepares the JSON payload for updating import records via API
   *
   * @param {Object} updatedItem - The complete updated item with all fields (input + database column names)
   * @param {Object} originalItem - The original item before edits (for change detection)
   * @param {Object} fieldMappingLookup - Mapping from input column names to database column names
   *                                     Example: { "NIT": "circuit_id", "Bundle Type": "service_type" }
   *
   * @returns {Object} payload - Clean JSON payload ready for API
   *
   * Payload Structure Rules:
   * 1. Only includes database column names (not input column names like "NIT")
   * 2. Excludes metadata fields (id, job_id, temp_id, import_status, etc.)
   * 3. Only includes fields that have actually changed from original
   * 4. Special fields (notes, follow_up_date) are always included if present
   * 5. Provider field is excluded in import mode (read-only context)
   *
   * Example payload output:
   * {
   *   "circuit_id": "ABC123",           // Mapped from "NIT" input column
   *   "service_type": "Premium",         // Mapped from "Bundle Type" input column
   *   "status": "Active",                // Direct database column
   *   "notes": "Updated notes",          // Special field (always included if present)
   *   "follow_up_date": "2024-12-31"     // Special field (always included if present)
   * }
   *
   * Note: This payload is sent to PUT /api/inventory-import/jobs/{jobId}/records/{recordId}
   */
  const prepareUpdatePayload = (
    updatedItem,
    originalItem,
    fieldMappingLookup,
  ) => {
    const payload = {};

    // Metadata fields to exclude
    const METADATA_FIELDS = [
      "id",
      "job_id",
      "temp_id",
      "import_status",
      "edited_columns",
      "raw_data",
      "created_at",
      "updated_at",
      "change_info",
      "record_id",
    ];

    // Special fields that don't need mapping (direct database columns)
    const SPECIAL_FIELDS = ["notes", "follow_up_date", "action", "verified"];

    // Backend allowed fields (fallback validation)
    const ALLOWED_FIELDS = [
      "provider",
      "category",
      "tenant_id",
      "circuit_id",
      "service_type",
      "status",
      "first_name",
      "last_name",
      "phone",
      "email",
      "bandwidth",
      "last_mile_provider",
      "provider_circuit_id",
      "contract_term",
      "end_of_term",
      "mrc",
      "billing_account",
      "works_with_circuit_id",
      "notes",
      "follow_up_date",
      "action",
      "verified",
      "street_address",
      "city",
      "state",
      "zip",
      "country",
    ];

    // Use fallback mapping if fieldMappingLookup is empty
    const effectiveMappingLookup =
      fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0
        ? fieldMappingLookup
        : FALLBACK_FIELD_MAPPING;

    // Track which database columns we've already processed to avoid duplicates
    const processedColumns = new Set();

    // Track field mapping transformations for debugging
    const fieldTransformations = [];

    // Helper function to normalize values for comparison
    const normalizeForComparison = (val) => {
      if (val === null || val === undefined) return null;
      // Handle boolean values - convert to string for consistent comparison
      if (typeof val === "boolean") {
        return val ? "true" : "false";
      }
      // Handle numeric boolean representations
      if (val === 1 || val === "1") return "true";
      if (val === 0 || val === "0") return "false";
      // Handle string boolean values
      if (typeof val === "string") {
        const trimmed = val.trim().toLowerCase();
        if (trimmed === "true" || trimmed === "1") return "true";
        if (trimmed === "false" || trimmed === "0" || trimmed === "")
          return "false";
        return trimmed;
      }
      return val;
    };

    // Helper function to check if field has changed
    const hasFieldChanged = (key, newValue, dbColumnName) => {
      if (!originalItem) {
        // If no original, include field if it has a value
        return newValue !== undefined && newValue !== null && newValue !== "";
      }

      // Check if field exists in original item (could be input or DB column name)
      const originalValueByInputKey = originalItem[key];
      const originalValueByDbKey = originalItem[dbColumnName];

      // Get original value (prefer input key, fallback to DB key)
      const originalValue =
        originalValueByInputKey !== undefined
          ? originalValueByInputKey
          : originalValueByDbKey;

      const normalizedOriginal = normalizeForComparison(originalValue);
      const normalizedNew = normalizeForComparison(newValue);

      // Special handling for notes, follow_up_date, and verified
      if (SPECIAL_FIELDS.includes(key)) {
        // For boolean fields like verified, check if value is explicitly set (not undefined)
        if (key === "verified") {
          // For verified, always include if value is explicitly set (true or false)
          if (newValue !== undefined) {
            // Check if it actually changed from original
            return normalizedOriginal !== normalizedNew;
          }
          return false;
        }
        // For notes and follow_up_date - always include if present
        // Always include special fields if they have a meaningful value
        // OR if they changed from original value
        if (newValue !== null && newValue !== undefined && newValue !== "") {
          return true; // Include non-empty values
        }
        // If clearing a field (had value, now empty)
        if (normalizedOriginal && !normalizedNew) {
          return true; // Include to clear the field
        }
        // Check if actually changed
        return normalizedOriginal !== normalizedNew;
      }

      // For other fields, only include if actually changed
      return normalizedOriginal !== normalizedNew;
    };

    // Detect if we're in import mode (import records have job_id, import_status, or temp_id)
    const isImportMode =
      updatedItem.job_id !== undefined ||
      updatedItem.import_status !== undefined ||
      updatedItem.temp_id !== undefined;

    // Iterate through all fields
    Object.keys(updatedItem).forEach((key) => {
      const value = updatedItem[key];

      // Skip metadata fields
      if (METADATA_FIELDS.includes(key)) {
        return;
      }

      // In import mode, skip provider field - it's read-only context from import, not something to update
      // The provider comes from the import context, not from the record data
      if (isImportMode && key.toLowerCase() === "provider") {
        return;
      }

      // Skip undefined values (but allow null and empty strings)
      if (value === undefined) {
        return;
      }

      let dbColumnName = null;

      // Determine the database column name with priority:
      // Priority 1: Special fields (notes, follow_up_date) - always use as-is
      if (SPECIAL_FIELDS.includes(key)) {
        dbColumnName = key;
      }
      // Priority 2: Check effectiveMappingLookup (handles input column names like "NIT" → "circuit_id")
      else if (effectiveMappingLookup && effectiveMappingLookup[key]) {
        dbColumnName = effectiveMappingLookup[key];
      }
      // Priority 3: Check if key is already a database column name (backward compatibility)
      else if (ALLOWED_FIELDS.includes(key)) {
        dbColumnName = key;
      }
      // Unknown field - skip
      else {
        console.warn(`Skipping unknown field: ${key}`);
        return;
      }

      // Check if field has changed before including it
      if (!hasFieldChanged(key, value, dbColumnName)) {
        // Field hasn't changed, skip it
        return;
      }

      // Validate that dbColumnName is in allowed fields
      if (!ALLOWED_FIELDS.includes(dbColumnName)) {
        console.warn(
          `Skipping field ${key} -> ${dbColumnName}: not in allowed fields`,
        );
        return;
      }

      // Skip if we've already processed this database column (prefer database column name over mapped name)
      if (processedColumns.has(dbColumnName)) {
        // If the current key is the database column name itself, prefer it over the mapped version
        if (ALLOWED_FIELDS.includes(key) && !effectiveMappingLookup?.[key]) {
          // Remove old entry and add new one to overwrite
          delete payload[dbColumnName];
        } else {
          // Skip - we already have a value for this database column from a mapped field
          return;
        }
      }

      // Mark this column as processed
      processedColumns.add(dbColumnName);

      // Clean and convert value
      let cleanedValue = value;

      // Get original value for comparison
      const originalValue =
        originalItem && originalItem[key] !== undefined
          ? originalItem[key]
          : originalItem && originalItem[dbColumnName] !== undefined
            ? originalItem[dbColumnName]
            : null;

      // Date fields
      if (dbColumnName === "end_of_term" || dbColumnName === "follow_up_date") {
        if (value === "" || value === null || value === undefined) {
          // Only convert to null if field was explicitly cleared (had value, now empty)
          cleanedValue =
            originalValue && originalValue !== null && originalValue !== ""
              ? null
              : value === "" || value === null
                ? null
                : undefined;
        } else if (value instanceof Date) {
          cleanedValue = formatDateLocal(value);
        } else if (typeof value === "string") {
          const date = new Date(value);
          cleanedValue = !isNaN(date.getTime()) ? formatDateLocal(date) : null;
        } else {
          cleanedValue = value;
        }
      }
      // Boolean fields
      else if (dbColumnName === "verified") {
        // Convert to boolean: true, "true", 1 → true; false, "false", 0, null, "" → false
        if (
          value === true ||
          value === "true" ||
          value === 1 ||
          value === "1"
        ) {
          cleanedValue = true;
        } else {
          cleanedValue = false;
        }
      }
      // Numeric fields
      else if (dbColumnName === "mrc" || dbColumnName === "contract_term") {
        if (value === "" || value === null || value === undefined) {
          // Only convert to null if field was explicitly cleared
          cleanedValue =
            originalValue && originalValue !== null && originalValue !== ""
              ? null
              : value === "" || value === null
                ? null
                : undefined;
        } else {
          const numValue = parseFloat(value);
          cleanedValue = isNaN(numValue) ? null : numValue;
        }
      }
      // Empty strings → null only if explicitly clearing a field that had a value
      else if (typeof value === "string" && value.trim() === "") {
        // If original had a value and now it's empty, this is an explicit clear → null
        if (originalValue && originalValue !== null && originalValue !== "") {
          cleanedValue = null;
        }
        // If original was also empty/null, this is no change → skip field (but we already checked hasFieldChanged)
        // But if we're here, hasFieldChanged returned true, so include it as null
        else {
          cleanedValue = null;
        }
      }
      // Preserve null values
      else if (value === null) {
        cleanedValue = null;
      }
      // Keep other values as-is
      else {
        cleanedValue = value;
      }

      // Add to payload with database column name
      payload[dbColumnName] = cleanedValue;

      // Track transformation for debugging
      fieldTransformations.push({
        inputKey: key,
        dbColumnName: dbColumnName,
        value: value,
        cleanedValue: cleanedValue,
        mapped: key !== dbColumnName,
        isSpecialField: SPECIAL_FIELDS.includes(key),
      });
    });

    // Debug logging for Phase 4 (can be removed in production)
    if (fieldTransformations.length > 0) {
      console.log("=== FIELD MAPPING TRANSFORMATIONS ===");
      console.log(
        "Original fieldMappingLookup available:",
        !!fieldMappingLookup,
      );
      console.log(
        "Original fieldMappingLookup size:",
        fieldMappingLookup ? Object.keys(fieldMappingLookup).length : 0,
      );
      console.log(
        "Effective mapping size:",
        Object.keys(effectiveMappingLookup).length,
      );
      console.log(
        "Using fallback mapping:",
        effectiveMappingLookup === FALLBACK_FIELD_MAPPING,
      );
      console.log("Original item provided:", !!originalItem);
      console.log("Transformations:", fieldTransformations);
      console.log("Final payload keys:", Object.keys(payload));
      console.log("Final payload:", payload);
      console.log("=====================================");
    }

    return payload;
  };

  const handlePreviewSave = async (updatedItem, changes) => {
    try {
      // Get the import ID from localStorage
      const importId = localStorage.getItem("lastImportId");
      if (!importId) {
        console.error("No import ID found in localStorage");
        setApiError("No import session found. Please re-import the file.");
        return;
      }

      // Check if record has a valid ID - this is required for API update
      let recordId = updatedItem.id;

      // Validate and parse ID as number
      if (recordId !== undefined && recordId !== null) {
        const parsedId = parseInt(recordId);
        if (!isNaN(parsedId) && parsedId > 0) {
          recordId = parsedId;
        } else {
          recordId = null;
        }
      } else {
        recordId = null;
      }

      // If ID is missing or invalid, try to find it from previewData by matching unique fields
      if (!recordId || recordId <= 0) {
        console.warn(
          "Record ID missing or invalid, attempting to find from previewData",
          {
            providedId: updatedItem.id,
            itemKeys: Object.keys(updatedItem),
          },
        );

        // Try to find ID from previewData by matching unique fields
        const matchingRecord = previewData.find((rec) => {
          // Match by id first (most reliable)
          if (rec.id && updatedItem.id && rec.id === updatedItem.id) {
            return true;
          }
          // Match by temp_id
          if (
            rec.temp_id &&
            updatedItem.temp_id &&
            rec.temp_id === updatedItem.temp_id
          ) {
            return true;
          }
          // Match by NIT
          if (rec.NIT && updatedItem.NIT && rec.NIT === updatedItem.NIT) {
            return true;
          }
          // Match by Circuit ID
          if (
            rec["Circuit ID"] &&
            updatedItem["Circuit ID"] &&
            rec["Circuit ID"] === updatedItem["Circuit ID"]
          ) {
            return true;
          }
          // Match by Service Number
          if (
            rec["Service Number"] &&
            updatedItem["Service Number"] &&
            rec["Service Number"] === updatedItem["Service Number"]
          ) {
            return true;
          }
          return false;
        });

        if (matchingRecord && matchingRecord.id) {
          const parsedMatchId = parseInt(matchingRecord.id);
          if (!isNaN(parsedMatchId) && parsedMatchId > 0) {
            recordId = parsedMatchId;
            updatedItem.id = recordId;
            console.log("Found record ID from previewData:", recordId);
          } else {
            recordId = null;
          }
        }
      }

      // Final validation: If ID is still missing, show user-friendly error
      if (!recordId || recordId <= 0) {
        const errorMessage =
          "Unable to save changes. The record identifier is missing. Please refresh the page and try again.";
        setApiError(errorMessage);
        alert(`⚠️ ${errorMessage}`);
        console.error("Cannot save record - ID is missing or invalid", {
          providedId: updatedItem.id,
          record_id: updatedItem.record_id,
          temp_id: updatedItem.temp_id,
          NIT: updatedItem.NIT,
          "Circuit ID": updatedItem["Circuit ID"],
          availableKeys: Object.keys(updatedItem),
        });
        return;
      }

      // Ensure updatedItem has the validated ID
      updatedItem.id = recordId;

      // Ensure the updated item has a unique identifier
      const uniqueId =
        updatedItem.temp_id || updatedItem.circuit_id || `record_${Date.now()}`;

      // Check if this record has been saved before
      const recordKey =
        updatedItem.circuit_id ||
        updatedItem.NIT ||
        updatedItem["Circuit ID"] ||
        uniqueId;
      const hasBeenSaved = savedRecords.has(recordKey);

      // Only mark as "updated" if the record was previously saved
      // New records should remain "new" even after editing
      const itemWithId = {
        ...updatedItem,
        temp_id: uniqueId,
        import_status: hasBeenSaved ? "updated" : "new",
      };

      // Use the validated recordId (already checked above)
      const databaseRecordId = recordId;

      console.log("=== SAVING WITH DATABASE ID ===");
      console.log("Saving changes for record:", {
        importId,
        databaseRecordId: databaseRecordId,
        recordIdType: typeof databaseRecordId,
        circuitId: updatedItem.circuit_id,
        NIT: updatedItem.NIT,
        changes: changes,
      });

      // Get original item from editingPreviewItem state
      const originalItem = editingPreviewItem?.original;

      // Ensure we have fieldMappingLookup - fetch metadata on-demand if empty
      let effectiveFieldMappingLookup = fieldMappingLookup;

      // If fieldMappingLookup is empty, try to fetch metadata
      if (
        !effectiveFieldMappingLookup ||
        Object.keys(effectiveFieldMappingLookup).length === 0
      ) {
        console.warn(
          "fieldMappingLookup is empty, attempting to fetch metadata...",
        );

        // Calculate effective provider before try block so it's accessible in catch
        const effectiveProviderForMetadata =
          category === "Custom" ? "Custom" : provider;

        try {
          if (importId && effectiveProviderForMetadata && category) {
            const metadata = await getImportJobMetadata(
              importId,
              effectiveProviderForMetadata,
              category,
              selectedTenantId,
            );
            if (metadata?.fieldMapping) {
              effectiveFieldMappingLookup = metadata.fieldMapping;
              console.log(
                "✅ Fetched metadata mapping:",
                Object.keys(effectiveFieldMappingLookup).length,
                "fields",
              );
            }
          }
        } catch (error) {
          // Log detailed error information for debugging
          const errorMessage =
            error.message ||
            error.details?.message ||
            error.details?.error ||
            "Unknown error";
          console.warn("Failed to fetch metadata, using fallback mapping:", {
            message: errorMessage,
            status: error.status,
            details: error.details,
            importId,
            provider: effectiveProviderForMetadata,
            category,
            tenantId: selectedTenantId,
          });
        }

        // Use fallback if still empty
        if (
          !effectiveFieldMappingLookup ||
          Object.keys(effectiveFieldMappingLookup).length === 0
        ) {
          console.warn("Using fallback field mapping");
          effectiveFieldMappingLookup = FALLBACK_FIELD_MAPPING;
        }
      }

      /**
       * JSON Payload for Import Record Update API
       *
       * cleanPayload structure: { databaseColumnName: value, ... }
       *
       * This is the final payload sent to the backend API endpoint:
       * PUT /api/inventory-import/jobs/{jobId}/records/{recordId}
       *
       * Payload Characteristics:
       * - Contains only database column names (not input column names)
       * - Contains only changed fields (not all fields)
       * - Excludes metadata fields (id, job_id, temp_id, etc.)
       * - Includes special fields (notes, follow_up_date) if present
       * - Values are the new values from the form
       *
       * Example payload:
       * {
       *   "circuit_id": "ABC123",
       *   "service_type": "Premium",
       *   "status": "Active",
       *   "notes": "User updated this record"
       * }
       */
      const cleanPayload = prepareUpdatePayload(
        updatedItem,
        originalItem,
        effectiveFieldMappingLookup,
      );

      // Phase 4: Validate payload
      if (Object.keys(cleanPayload).length === 0) {
        console.error("No valid fields to update", {
          updatedItemKeys: Object.keys(updatedItem),
          originalItemKeys: originalItem ? Object.keys(originalItem) : [],
          fieldMappingLookupSize: Object.keys(effectiveFieldMappingLookup)
            .length,
        });
        setApiError("No valid fields to update. Please check your changes.");
        alert(
          "❌ No changes detected. Please modify at least one field before saving.",
        );
        return;
      }

      // Check if payload only has null values (no actual changes)
      const hasNonNullChanges = Object.values(cleanPayload).some(
        (value) => value !== null && value !== undefined,
      );
      if (!hasNonNullChanges) {
        console.error("Payload contains only null values", cleanPayload);
        setApiError("No valid changes detected. All fields are null.");
        alert("❌ No valid changes detected. All fields are null.");
        return;
      }

      cleanPayload.version =
        updatedItem.version ??
        originalItem?.version ??
        itemWithId?.version ??
        null;

      // Phase 5: Debug logging (remove in production)
      console.log("=== TRANSFORMED PAYLOAD ===");
      console.log("Original item keys:", Object.keys(updatedItem));
      console.log("Original item sample:", {
        ...Object.fromEntries(
          Object.entries(updatedItem)
            .filter(([k]) => !["id", "job_id", "temp_id"].includes(k))
            .slice(0, 5),
        ),
      });
      console.log("Clean payload keys:", Object.keys(cleanPayload));
      console.log("Clean payload:", cleanPayload);
      console.log(
        "Effective field mapping lookup:",
        effectiveFieldMappingLookup,
      );
      console.log("Original item provided:", !!originalItem);
      console.log("Payload field count:", Object.keys(cleanPayload).length);
      console.log("===========================");

      // Call the API to update the import record using the database ID
      // Endpoint: PUT /api/inventory-import/jobs/{jobId}/records/{recordId}
      try {
        const response = await updateImportRecord(
          importId,
          databaseRecordId,
          cleanPayload, // ✅ Phase 4: Changed from updatedItem
        );
        console.log("✅ Successfully updated import record via API:", response);
        console.log("API Response structure:", {
          success: response.success,
          hasData: !!response.data,
          hasRecord: !!(response.data && response.data.record),
          updatedRecordId: response.data?.record?.id,
        });

        // Mark this record as saved
        setSavedRecords((prev) => new Set([...prev, recordKey]));

        // If API returns updated record, use that data
        let updatedRecord = itemWithId;
        if (response && response.data && response.data.record) {
          Object.assign(itemWithId, response.data.record);
          updatedRecord = response.data.record;
        }

        // Update previewData immediately with the saved changes
        // This ensures that if the modal is reopened, it shows the updated data
        setPreviewData((prevData) =>
          prevData.map((record) => {
            // Match by id or temp_id
            const recordIdMatch =
              (record.new && record.new.id === databaseRecordId) ||
              record.id === databaseRecordId;
            const tempIdMatch =
              (record.new && record.new.temp_id === uniqueId) ||
              record.temp_id === uniqueId;
            const circuitIdMatch =
              record.new &&
              updatedItem.circuit_id &&
              (record.new.circuit_id === updatedItem.circuit_id ||
                record.new.NIT === updatedItem.circuit_id ||
                record.new["Circuit ID"] === updatedItem.circuit_id);

            if (recordIdMatch || tempIdMatch || circuitIdMatch) {
              // Update both new and original to reflect the saved state
              return {
                ...record,
                new: updatedRecord,
                original: updatedRecord, // Update original to match new after save
              };
            }
            return record;
          }),
        );

        // Update editingPreviewItem if it's the same record being edited
        if (editingPreviewItem) {
          const isSameRecord =
            (editingPreviewItem.new &&
              (editingPreviewItem.new.id === databaseRecordId ||
                editingPreviewItem.new.temp_id === uniqueId)) ||
            (editingPreviewItem.original &&
              (editingPreviewItem.original.id === databaseRecordId ||
                editingPreviewItem.original.temp_id === uniqueId));

          if (isSameRecord) {
            setEditingPreviewItem({
              new: updatedRecord,
              original: updatedRecord, // Update original to match new after save
            });
          }
        }

        // Phase 1: Reload page data to reflect changes from database
        console.log("🔄 Reloading page data to reflect changes...");
        try {
          await loadPage(importId, currentPage, pageSize);
          console.log("✅ Page data reloaded successfully");
        } catch (reloadError) {
          console.error("Failed to reload page data:", reloadError);
          alert(
            "⚠️ Changes saved, but failed to refresh view. Please refresh the page manually.",
          );
          // Don't throw - the save was successful, just the refresh failed
        }
      } catch (apiError) {
        console.error("API update failed:", apiError);
        console.error("Payload sent:", cleanPayload);
        console.error("Updated item:", updatedItem);
        console.error("Original item:", originalItem);
        console.error(
          "Effective field mapping lookup:",
          effectiveFieldMappingLookup,
        );
        throw apiError; // Re-throw to be caught by outer catch
      }

      // Phase 2: Remove stale state update - reload handles this now
      // REMOVED: Old approach that used stale data
      // The loadPage() call above will fetch fresh data from the database
      // and update the previewData state with the correct values

      // Track changes if any (for future use)
      if (changes && Object.keys(changes).length > 0) {
        console.log("Changes tracked:", {
          uniqueId,
          circuitId: updatedItem.circuit_id,
          changes: changes,
          timestamp: new Date().toISOString(),
        });
      }

      setEditingPreviewItem(null);

      // Defer toast so it shows after modal unmounts and is clearly visible
      setTimeout(() => {
        setToast({ message: "Changes saved successfully!", type: "success" });
      }, 0);
    } catch (error) {
      console.error("Failed to save import record:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        updatedItem: updatedItem,
      });

      if (error.status === 409) {
        const conflictMessage =
          "This import record was updated by someone else. Please reload and try again.";
        setApiError(conflictMessage);
        setTimeout(() => {
          setToast({ title: "Changes Not Saved", message: conflictMessage, type: "warning" });
        }, 0);
        try {
          const conflictImportId =
            currentImportId || localStorage.getItem("lastImportId");
          if (conflictImportId) {
            await loadPage(conflictImportId, currentPage, pageSize);
          }
        } catch (_) {
          /* refresh best-effort */
        }
        setEditingPreviewItem(null);
        return;
      }

      // Show generic error message for 400, 500, and network errors
      let errorMessage = error.message || "Failed to save changes";
      if (error.status === 400 || error.status === 500) {
        errorMessage =
          "Unable to process your request right now. Please try again later.";
      } else if (
        error.isNetworkError ||
        error.message === "Unable to load the data"
      ) {
        errorMessage = "Unable to load the data";
      } else if (error.status === 404) {
        errorMessage =
          "Record not found. Please refresh the page and try again.";
      }

      setApiError(errorMessage);
      setTimeout(() => {
        setToast({ message: errorMessage, type: "error" });
      }, 0);
    }
  };

  // Normalize category using constants
  const normalizeCategory = (category) => {
    if (!category) return "Other";
    const categoryStr = String(category);
    const match = CATEGORIES.find(
      (c) => c && String(c).toLowerCase() === categoryStr.toLowerCase(),
    );
    return match || category;
  };

  // Normalize provider using constants
  const normalizeProvider = (provider) => {
    if (!provider) return "-";
    const providerStr = String(provider);
    const match = PROVIDERS.find(
      (p) => p && String(p).toLowerCase() === providerStr.toLowerCase(),
    );
    return match || provider;
  };

  // Use grouped data directly from API (backend-driven grouping and sorting)
  // No client-side sorting needed - backend handles it
  const groupedInventory = useMemo(() => {
    if (!groupedInventoryData || groupedInventoryData.length === 0) {
      return [];
    }

    // Return data as-is from backend (already sorted)
    return groupedInventoryData;
  }, [groupedInventoryData]);

  const toggleAddress = (addressKey) => {
    setExpandedAddresses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(addressKey)) {
        newSet.delete(addressKey);
      } else {
        newSet.add(addressKey);
      }
      return newSet;
    });
  };

  const isExpanded = (addressKey) => expandedAddresses.has(addressKey);

  // Handle sort button clicks - triggers backend reload
  const handleSortClick = async (columnName, direction) => {
    try {
      // If clicking the same direction, reset sort
      if (
        sortConfig.sortBy === columnName &&
        sortConfig.sortOrder === direction
      ) {
        setSortConfig({ sortBy: null, sortOrder: null });
        // Reload page with no sorting
        const importId = localStorage.getItem("lastImportId");
        if (importId) {
          await loadPage(importId, currentPage, pageSize, null, null);
        }
      } else {
        // Set new sort direction and reload from backend
        // Pass sort values directly to loadPage to avoid state timing issues
        setSortConfig({ sortBy: columnName, sortOrder: direction });
        const importId = localStorage.getItem("lastImportId");
        if (importId) {
          await loadPage(importId, 1, pageSize, columnName, direction); // Reset to page 1 when sorting changes
        }
      }
    } catch (error) {
      console.error("Error sorting addresses:", error);
    }
  };

  // Render sort buttons for address column
  const renderSortButtons = () => {
    const isAscActive =
      sortConfig.sortBy === "address" && sortConfig.sortOrder === "asc";
    const isDescActive =
      sortConfig.sortBy === "address" && sortConfig.sortOrder === "desc";

    // Get active color class - use blue as default if tenant color not available
    const activeColorClass = tenant?.primaryColor
      ? tenant.primaryColor === "blue"
        ? "text-blue-600"
        : tenant.primaryColor === "green"
          ? "text-green-600"
          : tenant.primaryColor === "purple"
            ? "text-purple-600"
            : tenant.primaryColor === "red"
              ? "text-red-600"
              : "text-blue-600"
      : "text-blue-600";

    return (
      <div className="inline-flex flex-col ml-1.5 gap-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleSortClick("address", "asc");
          }}
          className={`p-1 hover:bg-gray-300 rounded transition-colors cursor-pointer ${
            isAscActive
              ? `${activeColorClass} bg-gray-200`
              : "text-gray-500 hover:text-gray-700"
          }`}
          title="Sort addresses ascending"
          aria-label="Sort address ascending"
          type="button"
        >
          <ArrowUpIcon className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleSortClick("address", "desc");
          }}
          className={`p-1 hover:bg-gray-300 rounded transition-colors cursor-pointer ${
            isDescActive
              ? `${activeColorClass} bg-gray-200`
              : "text-gray-500 hover:text-gray-700"
          }`}
          title="Sort addresses descending"
          aria-label="Sort address descending"
          type="button"
        >
          <ArrowDownIcon className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Format address for display (1 line)
  const formatAddressDisplay = (group) => {
    // If group already has formatted address, use it
    if (group.address && group.address !== "-") {
      return group.address;
    }

    // Check if first item has standard_address or raw fields
    const firstItem =
      group.items && group.items.length > 0 ? group.items[0] : null;
    const standardAddress = firstItem?.standard_address;

    // Prefer std_*; fall back to raw input when unlinked (needs_master)
    const address = formatConcatenatedAddress({
      std_street_address:
        group.std_street_address || firstItem?.street_address || "",
      std_city: group.std_city || firstItem?.city || "",
      std_state: group.std_state || firstItem?.state || "",
      std_zip: group.std_zip || firstItem?.zip || "",
      std_country: group.std_country || firstItem?.country || "",
      standard_address: standardAddress,
    });
    return address || "-";
  };

  // Check if Provider or Category columns have any data to display
  // Always show these columns if we have provider/category from dropdown or file data
  const hasProviderData = useMemo(() => {
    // Always show provider column if we have provider from dropdown
    if (provider && provider !== "") {
      return true;
    }
    // Also check if any row has provider data from file
    if (!safePreviewData || !Array.isArray(safePreviewData)) {
      return false;
    }
    return safePreviewData.some((row) => {
      return row.provider && row.provider !== null && row.provider !== "";
    });
  }, [safePreviewData, provider]);

  const hasCategoryData = useMemo(() => {
    // Always show category column if we have category from dropdown
    if (category && category !== "") {
      return true;
    }
    // Also check if any row has category data from file
    if (!safePreviewData || !Array.isArray(safePreviewData)) {
      return false;
    }
    return safePreviewData.some((row) => {
      return row.category && row.category !== null && row.category !== "";
    });
  }, [safePreviewData, category]);

  // Helper function to check if verified is true (handles all formats)
  const isVerified = (value) => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return false;
    }

    // Handle boolean true
    if (value === true) {
      return true;
    }

    // Handle numeric 1
    if (value === 1 || value === "1") {
      return true;
    }

    // Handle string "true" (case-insensitive)
    if (typeof value === "string") {
      const trimmed = value.trim().toLowerCase();
      return trimmed === "true" || trimmed === "1";
    }

    // Handle numeric 0 or false as false
    if (value === false || value === 0 || value === "0") {
      return false;
    }

    // Default to false for any other value
    return false;
  };

  // Format bandwidth value - now uses utility function from helpers

  // Get status color class (from GroupedInventoryTable)
  const getStatusColor = (status) => {
    if (!status) return "text-gray-500";
    const statusLower = String(status).toLowerCase().trim();

    // Check for Pending variations FIRST (before checking for "disconnect" alone)
    // Gray: Pending/Inactive/Suspended statuses
    if (
      statusLower === "pending" ||
      statusLower === "pending install" ||
      statusLower === "pending disconnect" ||
      statusLower.startsWith("pending")
    ) {
      return "text-gray-500";
    }

    // Green: Active/In Service statuses
    if (statusLower === "in service" || statusLower === "active") {
      return "text-green-600 font-medium";
    }

    // Red: Canceled/Cancelled statuses
    if (
      statusLower === "canceled" ||
      statusLower === "cancelled" ||
      statusLower.includes("cancel")
    ) {
      return "text-red-600 font-medium";
    }

    // Red: Disconnected (but not "pending disconnect" which is already handled above)
    if (
      statusLower === "disconnected" ||
      (statusLower.includes("disconnect") && !statusLower.includes("pending"))
    ) {
      return "text-red-600 font-medium";
    }

    // Gray: Inactive/Suspended statuses
    if (
      statusLower === "inactive" ||
      statusLower === "in active" ||
      statusLower.includes("inactive") ||
      statusLower === "suspended" ||
      statusLower.includes("suspend")
    ) {
      return "text-gray-500";
    }

    // Default to gray for unknown statuses
    return "text-gray-500";
  };

  // Format service information for display (from GroupedInventoryTable)
  // Service number = circuit_id, so we show: [circuit_id] - [provider] - [category] - [bandwidth]
  // For expanded rows (excludeProviderCategory=true): [circuit_id] - [name] - [email] - [mrc] - [bandwidth]
  const formatServiceInfo = (item, excludeProviderCategory = false) => {
    // Get circuit_id - try multiple field names
    const circuitId =
      item.circuit_id ||
      item.NIT ||
      item["Circuit ID"] ||
      item["Service Number"] ||
      "-";

    // Get category for phone number formatting
    const itemCategory = item.category || category || "Other";
    const normalizedCategory = normalizeCategory(itemCategory);
    const shouldFormatAsPhone =
      normalizedCategory === "Mobility" ||
      normalizedCategory === "Analog" ||
      normalizedCategory === "Voice";

    // For expanded rows, show Service Number, Name, Email, Bandwidth (exclude Provider/Category and MRC)
    // MRC is shown separately in the right column
    if (excludeProviderCategory) {
      const parts = [];

      // Service Number (circuit_id) - format as phone for Mobility/Analog
      if (circuitId && circuitId !== "-") {
        const formattedCircuitId = shouldFormatAsPhone
          ? formatCircuitIdForDisplay (circuitId)
          : circuitId;
        parts.push(formattedCircuitId);
      }

      // Name (first_name + last_name)
      const firstName = item.first_name || item["User First Name"] || "";
      const lastName = item.last_name || item["User Last Name"] || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      if (fullName) parts.push(fullName);

      // Email
      const email = item.email || item["User Email"] || "";
      if (email) parts.push(email);

      // Bandwidth
      const bandwidthValue =
        item.bandwidth ||
        item["Bandwidth"] ||
        item["Bandwidth Capacity"] ||
        null;
      const bandwidthCapacityValue =
        item.bandwidth_capacity || item["Bandwidth Capacity"] || null;
      const bandwidth = formatBandwidth(bandwidthValue, bandwidthCapacityValue);
      if (bandwidth && bandwidth !== "-") parts.push(bandwidth);

      return parts.length > 0 ? parts.join(" - ") : "-";
    }

    // For address row summary, show: [circuit_id] - [provider] - [category] - [bandwidth]
    const itemProvider =
      item.provider || (category === "Custom" ? "Custom" : provider) || "-";
    const normalizedProvider = normalizeProvider(itemProvider);

    // Use itemCategory and normalizedCategory already defined at function start

    const bandwidthValue =
      item.bandwidth || item["Bandwidth"] || item["Bandwidth Capacity"] || null;
    const bandwidthCapacityValue =
      item.bandwidth_capacity || item["Bandwidth Capacity"] || null;
    const bandwidth = formatBandwidth(bandwidthValue, bandwidthCapacityValue);

    const parts = [];
    // Format circuit_id as phone for Mobility/Analog categories
    if (circuitId && circuitId !== "-") {
      const formattedCircuitId = shouldFormatAsPhone
        ? formatCircuitIdForDisplay (circuitId)
        : circuitId;
      parts.push(formattedCircuitId);
    }
    if (normalizedProvider && normalizedProvider !== "-")
      parts.push(normalizedProvider);
    if (
      normalizedCategory &&
      normalizedCategory !== "-" &&
      normalizedCategory !== "Other"
    )
      parts.push(normalizedCategory);
    if (bandwidth && bandwidth !== "-") parts.push(bandwidth);

    return parts.length > 0 ? parts.join(" - ") : "-";
  };

  /**
   * Builds originalItem from modified_fields for "updated" records
   * Preserves original database values before any edits
   * @param {Object} row - Current row data from previewData
   * @param {Object} fieldMappingLookup - Mapping from input column to database column
   * @returns {Object} originalItem with original database values
   */
  const buildOriginalItemFromModifiedFields = (
    row,
    fieldMappingLookup = {},
  ) => {
    // For "new" or "same" records, use row as-is
    if (row.import_status !== "updated" || !row.modified_fields) {
      return { ...row };
    }

    try {
      // Parse modified_fields (can be string or object)
      const modifiedFields =
        typeof row.modified_fields === "string"
          ? JSON.parse(row.modified_fields)
          : row.modified_fields;

      if (!modifiedFields || typeof modifiedFields !== "object") {
        console.warn("Invalid modified_fields structure, using row as-is");
        return { ...row };
      }

      // Start with current row values as base (preserves all fields)
      const originalItem = { ...row };

      // Override ONLY the fields that have old_value in modified_fields
      // This ensures unchanged fields still have their current values
      Object.keys(modifiedFields).forEach((fieldName) => {
        const fieldData = modifiedFields[fieldName];

        // Validate fieldData structure
        if (
          fieldData &&
          typeof fieldData === "object" &&
          fieldData.changed &&
          fieldData.old_value !== undefined
        ) {
          // Set the original value under the database column name
          originalItem[fieldName] = fieldData.old_value;

          // Also set under mapped input column name if fieldMappingLookup exists
          // This ensures the modal can find the value by either name
          if (
            fieldMappingLookup &&
            Object.keys(fieldMappingLookup).length > 0
          ) {
            Object.entries(fieldMappingLookup).forEach(([inputCol, dbCol]) => {
              if (dbCol === fieldName) {
                originalItem[inputCol] = fieldData.old_value;
              }
            });
          }
        }
      });

      console.log("✅ Built originalItem from modified_fields:", {
        import_status: row.import_status,
        modifiedFieldsCount: Object.keys(modifiedFields).length,
        originalItemSample: {
          mrc: originalItem.mrc || originalItem.MRC,
          circuit_id: originalItem.circuit_id || originalItem.NIT,
        },
      });

      return originalItem;
    } catch (error) {
      console.error("❌ Failed to parse modified_fields:", error);
      console.error("Row data:", row);
      // Fallback to using row as-is on error
      return { ...row };
    }
  };

  const handleEditClick = async (row) => {
    // Guard: Ensure row is valid
    if (!row) {
      console.error("handleEditClick: row is undefined or null");
      return;
    }

    // Guard: Ensure required functions are available
    if (typeof buildOriginalItemFromModifiedFields !== "function") {
      console.error(
        "handleEditClick: buildOriginalItemFromModifiedFields is not a function",
      );
      return;
    }

    if (typeof getImportRecord !== "function") {
      console.error("handleEditClick: getImportRecord is not a function");
      return;
    }

    // Phase 1: Debug logging to inspect data structure
    console.log("🔍 DEBUG: handleEditClick - Row data:", {
      import_status: row.import_status,
      has_modified_fields: !!row.modified_fields,
      modified_fields_type: typeof row.modified_fields,
      modified_fields_sample: row.modified_fields
        ? typeof row.modified_fields === "string"
          ? row.modified_fields.substring(0, 200)
          : JSON.stringify(row.modified_fields).substring(0, 200)
        : null,
      row_keys: Object.keys(row).slice(0, 10),
    });

    // Phase 3: Build originalItem from modified_fields if available (for "updated" records)
    // This preserves the original database values before any edits
    const originalItem = buildOriginalItemFromModifiedFields(
      row,
      fieldMappingLookup || {},
    );

    // CRITICAL: Validate that database ID exists BEFORE opening edit modal
    // Extract and validate the ID
    let recordId = row.id;

    // Try to parse as number if it's a string
    if (recordId !== undefined && recordId !== null) {
      const parsedId = parseInt(recordId);
      if (!isNaN(parsedId) && parsedId > 0) {
        recordId = parsedId;
      }
    }

    // If ID is still missing, try to find it from previewData by matching unique fields
    if (!recordId || recordId <= 0) {
      console.warn(
        "Record ID missing from row, attempting to find from previewData",
      );

      // Guard: Ensure previewData is an array
      if (!Array.isArray(previewData)) {
        console.error(
          "handleEditClick: previewData is not an array",
          previewData,
        );
        const errorMessage =
          "Unable to edit this record. The record identifier is missing. Please refresh the page and try again.";
        setApiError(errorMessage);
        alert(`⚠️ ${errorMessage}`);
        return;
      }

      const matchingRecord = previewData.find((rec) => {
        // Match by id first (most reliable)
        if (rec.id && row.id && rec.id === row.id) {
          return true;
        }
        // Match by temp_id
        if (rec.temp_id && row.temp_id && rec.temp_id === row.temp_id) {
          return true;
        }
        // Match by NIT
        if (rec.NIT && row.NIT && rec.NIT === row.NIT) {
          return true;
        }
        // Match by Circuit ID
        if (
          rec["Circuit ID"] &&
          row["Circuit ID"] &&
          rec["Circuit ID"] === row["Circuit ID"]
        ) {
          return true;
        }
        // Match by Service Number
        if (
          rec["Service Number"] &&
          row["Service Number"] &&
          rec["Service Number"] === row["Service Number"]
        ) {
          return true;
        }
        return false;
      });

      if (matchingRecord && matchingRecord.id) {
        const parsedMatchId = parseInt(matchingRecord.id);
        if (!isNaN(parsedMatchId) && parsedMatchId > 0) {
          recordId = parsedMatchId;
          console.log("Found record ID from previewData:", recordId);
        }
      }
    }

    // Final validation: If ID is still missing, show error and prevent modal from opening
    if (!recordId || recordId <= 0) {
      const errorMessage =
        "Unable to edit this record. The record identifier is missing. Please refresh the page and try again.";
      setApiError(errorMessage);
      alert(`⚠️ ${errorMessage}`);
      console.error("Cannot edit record - ID is missing. Row data:", {
        id: row.id,
        record_id: row.record_id,
        temp_id: row.temp_id,
        NIT: row.NIT,
        "Circuit ID": row["Circuit ID"],
        availableKeys: Object.keys(row),
      });
      return; // Prevent modal from opening
    }

    // Fetch full record from backend to ensure we have all fields including notes and follow_up_date
    try {
      const importId = localStorage.getItem("lastImportId");
      if (!importId) {
        console.error("No import ID found in localStorage");
        alert(
          "⚠️ Cannot edit: Import session not found. Please refresh the page.",
        );
        return;
      }

      console.log("Fetching full record from backend:", {
        importId,
        recordId,
      });
      const fullRecordResponse = await getImportRecord(importId, recordId);

      // Extract record from response - handle both response.data.record and response.data formats
      const fullRecord =
        fullRecordResponse?.data?.record ||
        fullRecordResponse?.data ||
        fullRecordResponse ||
        null;

      console.log("Fetched full record:", fullRecord);

      if (fullRecord && fullRecord.id) {
        // Use the full record from backend which includes all fields (notes, follow_up_date, etc.)
        const rowWithId = {
          ...row, // Preserve original row data for any fields not in backend response
          ...fullRecord, // Override with full record from backend (includes notes, follow_up_date)
          // Ensure database ID is preserved
          id: recordId,
          temp_id:
            row.temp_id ||
            fullRecord.temp_id ||
            row.circuit_id ||
            `record_${Date.now()}`,
        };

        // Debug logging (only in development)
        if (process.env.NODE_ENV !== "production") {
          console.log("=== EDIT CLICK - FULL RECORD FETCHED ===");
          console.log("Row database ID:", rowWithId.id, typeof rowWithId.id);
          console.log(
            "Has notes:",
            !!rowWithId.notes,
            "Value:",
            rowWithId.notes,
          );
          console.log(
            "Has follow_up_date:",
            !!rowWithId.follow_up_date,
            "Value:",
            rowWithId.follow_up_date,
          );
          console.log(
            "Has verified:",
            !!rowWithId.verified,
            "Value:",
            rowWithId.verified,
            "Type:",
            typeof rowWithId.verified,
          );
          console.log("===================================");
        }

        setEditingPreviewItem({ new: rowWithId, original: originalItem });
      } else {
        console.error(
          "Failed to fetch full record or record is invalid:",
          fullRecordResponse,
        );
        // Fallback to using row data if fetch fails
        const rowWithId = {
          ...row,
          id: recordId,
          temp_id: row.temp_id || row.circuit_id || `record_${Date.now()}`,
        };
        setEditingPreviewItem({ new: rowWithId, original: originalItem });
      }
    } catch (fetchError) {
      console.error("Failed to fetch full record from backend:", fetchError);
      // Fallback to using row data if fetch fails
      const rowWithId = {
        ...row,
        id: recordId,
        temp_id: row.temp_id || row.circuit_id || `record_${Date.now()}`,
      };
      setEditingPreviewItem({ new: rowWithId, original: originalItem });
    }
  };

  // Get import status badge color and text
  const getImportStatusBadge = (import_status) => {
    if (!import_status) return { color: "text-gray-500", text: "" };
    const statusLower = String(import_status).toLowerCase().trim();

    if (statusLower === "new") {
      return { color: "text-green-600 font-medium", text: "NEW" };
    } else if (statusLower === "updated") {
      return { color: "text-yellow-600 font-medium", text: "UPDATED" };
    } else if (statusLower === "same") {
      return { color: "text-gray-500", text: "SAME" };
    } else if (statusLower === "error") {
      return { color: "text-red-600 font-medium", text: "ERROR" };
    }
    return { color: "text-gray-500", text: statusLower.toUpperCase() };
  };

  // Helper function to get field value from row using column name and mapping
  const getFieldValue = (row, columnName) => {
    if (!columnName) return null;

    // Direct access
    if (
      row[columnName] !== undefined &&
      row[columnName] !== null &&
      row[columnName] !== ""
    ) {
      return row[columnName];
    }

    // Try field mapping lookup
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      const dbColumn = fieldMappingLookup[columnName];
      if (
        dbColumn &&
        row[dbColumn] !== undefined &&
        row[dbColumn] !== null &&
        row[dbColumn] !== ""
      ) {
        return row[dbColumn];
      }
    }

    // Try common variations for known fields
    const lowerCol = columnName.toLowerCase();
    if (
      lowerCol.includes("circuit") ||
      lowerCol.includes("nit") ||
      lowerCol.includes("service number")
    ) {
      return (
        row.circuit_id ||
        row.NIT ||
        row["Circuit ID"] ||
        row["Service Number"] ||
        null
      );
    }
    if (lowerCol.includes("bandwidth")) {
      return (
        row.bandwidth || row["Bandwidth"] || row["Bandwidth Capacity"] || null
      );
    }
    if (
      lowerCol === "mrc" ||
      lowerCol.includes("total") ||
      lowerCol.includes("usd")
    ) {
      return row.mrc || row.MRC || row["Total $"] || row["USD Amount"] || null;
    }
    if (lowerCol.includes("term") && !lowerCol.includes("end")) {
      return (
        row.contract_term ||
        row["Term"] ||
        row["Contract Term (months)"] ||
        null
      );
    }
    if (lowerCol.includes("end") && lowerCol.includes("term")) {
      return row.end_of_term || row["End of Term"] || row["Expiration"] || null;
    }
    if (lowerCol === "address") {
      return (
        formatConcatenatedAddress({
          std_street_address: row.std_street_address,
          std_city: row.std_city,
          std_state: row.std_state,
          std_zip: row.std_zip,
          std_country: row.std_country,
        }) || "-"
      );
    }

    return null;
  };

  // Helper function to format field value for display
  const formatFieldValue = (value, columnName) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const lowerCol = columnName?.toLowerCase() || "";

    // Format currency for MRC
    if (isMRCColumn(columnName, fieldMappingLookup)) {
      return formatCurrency(value);
    }

    // Format dates
    if (
      lowerCol.includes("date") ||
      (lowerCol.includes("term") && lowerCol.includes("end"))
    ) {
      if (value instanceof Date) {
        return formatDateLocal(value);
      }
      if (typeof value === "string" && value.trim() !== "") {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return formatDateLocal(date);
        }
      }
    }

    // Format bandwidth
    if (lowerCol.includes("bandwidth")) {
      return formatBandwidth(value);
    }

    // Return as string
    return String(value);
  };

  // Cross-reference values are pipe-joined circuit IDs, so their cell wraps onto
  // several lines rather than stretching the table wider than the viewport.
  const isWorksWithCircuitIdColumn = (columnName) => {
    if (!columnName) return false;
    const normalized = String(columnName).trim().toLowerCase();
    return (
      normalized === "works_with_circuit_id" ||
      normalized === "works with circuit id" ||
      fieldMappingLookup?.[columnName] === "works_with_circuit_id"
    );
  };

  // The wrapping column can only claim the leftover width if the table is allowed
  // to shrink to its container, so max-content sizing is dropped just for it.
  const hasWorksWithCircuitIdColumn = (orderedColumns || []).some((col) =>
    isWorksWithCircuitIdColumn(col),
  );

  // Render table header row for expanded services
  const renderTableHeader = () => {
    if (!orderedColumns || orderedColumns.length === 0) {
      return null;
    }

    // Filter out "address" column since it's already shown in the group header
    const filteredColumns = orderedColumns.filter(
      (col) => col.toLowerCase() !== "address",
    );

    // Show Provider and Category columns for Custom category (they come from file data)
    const showProviderColumn = category === "Custom" && hasProviderData;
    const showCategoryColumn = category === "Custom" && hasCategoryData;

    return (
      <thead className="bg-gray-100 sticky top-0 z-10">
        <tr>
          {/* Import Status column */}
          <th
            scope="col"
            className="text-[14px] font-bold px-3 py-2 text-left whitespace-nowrap align-middle"
            style={{ color: "#374151" }}
          >
            Status
          </th>
          {/* Provider column (for Custom category only) */}
          {showProviderColumn && (
            <th
              scope="col"
              className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-left whitespace-nowrap align-middle"
              style={{ color: "#374151" }}
            >
              Provider
            </th>
          )}
          {/* Category column (for Custom category only) */}
          {showCategoryColumn && (
            <th
              scope="col"
              className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-left whitespace-nowrap align-middle"
              style={{ color: "#374151" }}
            >
              Category
            </th>
          )}
          {/* Dynamic columns */}
          {filteredColumns.map((col) => (
            <th
              scope="col"
              key={col}
              className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-left whitespace-nowrap align-middle"
              style={{ color: "#374151" }}
            >
              {getInventoryColumnLabel(
                col,
                fieldMappingLookup,
                INVENTORY_FIELD_LABELS,
              )}
            </th>
          ))}
          {/* MRC column (right side) */}
          <th
            scope="col"
            className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-left whitespace-nowrap align-middle"
            style={{ color: "#374151" }}
          >
            MRC
          </th>
          {/* Edit column */}
          <th
            scope="col"
            className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-center whitespace-nowrap align-middle"
            style={{ color: "#374151" }}
          >
            Edit
          </th>
          {/* Verified column */}
          <th
            scope="col"
            className="border-l border-gray-300 text-[14px] font-bold px-3 py-2 text-center whitespace-nowrap align-middle"
            style={{ color: "#374151" }}
          >
            ✓
          </th>
        </tr>
      </thead>
    );
  };

  // Render a single import record row with all fields
  const renderItemRow = (row, index) => {
    const uniqueKey = row.temp_id || row.circuit_id || row.id || `row_${index}`;

    // Get status - try multiple field names
    const status = row.status || row["Circuit Status Code"] || "-";

    // Get MRC - comprehensive extraction using fieldMappingLookup for Custom category support
    let mrc = null;

    // Strategy 1: Use fieldMappingLookup to find input column that maps to "mrc"
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      // Find all input columns that map to "mrc"
      const mrcInputColumns = Object.entries(fieldMappingLookup)
        .filter(([inputCol, dbCol]) => dbCol === "mrc")
        .map(([inputCol]) => inputCol);

      // Try each input column name
      for (const inputCol of mrcInputColumns) {
        if (
          row[inputCol] !== undefined &&
          row[inputCol] !== null &&
          row[inputCol] !== ""
        ) {
          mrc = row[inputCol];
          break;
        }
      }
    }

    // Strategy 2: Try direct database column names
    if (!mrc) {
      mrc = row.mrc || row.MRC || null;
    }

    // Strategy 3: Try common input column names
    if (!mrc) {
      mrc = row["Total $"] || row["Total$"] || row["USD Amount"] || null;
    }

    // Strategy 4: Try lowercase variations
    if (!mrc) {
      const rowKeys = Object.keys(row);
      for (const key of rowKeys) {
        const keyLower = key.toLowerCase().trim();
        if (
          keyLower === "mrc" ||
          keyLower === "total $" ||
          keyLower === "total$" ||
          keyLower === "usd amount"
        ) {
          mrc = row[key];
          break;
        }
      }
    }

    // Get import status badge
    const importStatusBadge = getImportStatusBadge(row.import_status);

    // Get Provider and Category values for Custom category (from file data)
    const rowProvider =
      row.provider || (category === "Custom" ? "Custom" : provider) || "-";
    const rowCategory = row.category || category || "-";

    // Show Provider and Category columns for Custom category (they come from file data)
    const showProviderColumn = category === "Custom" && hasProviderData;
    const showCategoryColumn = category === "Custom" && hasCategoryData;

    return (
      <tr
        key={uniqueKey}
        className="hover:bg-gray-50 transition-colors border-b border-gray-200"
      >
        {/* Import Status Badge Column */}
        <td className="px-3 py-2.5 text-[14px] whitespace-nowrap align-middle border-b border-gray-200">
          {importStatusBadge.text && (
            <div className="flex items-center justify-start">
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  importStatusBadge.color === "text-green-600 font-medium"
                    ? "bg-green-100 text-green-700"
                    : importStatusBadge.color === "text-yellow-600 font-medium"
                      ? "bg-yellow-100 text-yellow-700"
                      : importStatusBadge.color === "text-red-600 font-medium"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-700"
                }`}
              >
                {importStatusBadge.text}
              </span>
            </div>
          )}
        </td>

        {/* Provider column (for Custom category only) */}
        {showProviderColumn && (
          <td
            className="border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-[14px] whitespace-nowrap align-middle"
            style={{ color: "#000000" }}
          >
            {normalizeProvider(rowProvider)}
          </td>
        )}

        {/* Category column (for Custom category only) */}
        {showCategoryColumn && (
          <td
            className="border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-[14px] whitespace-nowrap align-middle"
            style={{ color: "#000000" }}
          >
            {normalizeCategory(rowCategory)}
          </td>
        )}

        {/* Dynamic columns from orderedColumns */}
        {orderedColumns && orderedColumns.length > 0 ? (
          (() => {
            // Filter out "address" column since it's already shown in the group header
            const filteredColumns = orderedColumns.filter(
              (col) => col.toLowerCase() !== "address",
            );
            return filteredColumns.map((col) => {
              const value = getFieldValue(row, col);
              // Check if this column is circuit_id and category is Mobility/Analog/Voice
              const isCircuitIdColumn =
                col.toLowerCase().includes("circuit") ||
                col.toLowerCase().includes("nit") ||
                col.toLowerCase().includes("service number") ||
                fieldMappingLookup?.[col] === "circuit_id";
              const shouldFormatAsPhone =
                isCircuitIdColumn &&
                (rowCategory === "Mobility" ||
                  rowCategory === "Analog" ||
                  rowCategory === "Voice" ||
                  category === "Mobility" ||
                  category === "Analog" ||
                  category === "Voice");
              const formattedValue = shouldFormatAsPhone
                ? formatCircuitIdForDisplay (value)
                : formatFieldValue(value, col);
              const shouldWrapValue = isWorksWithCircuitIdColumn(col);
              return (
                <td
                  key={col}
                  className={`border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-[14px] ${
                    shouldWrapValue
                      ? "whitespace-normal align-top"
                      : "whitespace-nowrap align-middle"
                  }`}
                  style={{ color: "#000000" }}
                >
                  {shouldWrapValue ? (
                    <div className="w-full min-w-[240px] break-words">
                      {formattedValue}
                    </div>
                  ) : (
                    formattedValue
                  )}
                </td>
              );
            });
          })()
        ) : (
          <td
            colSpan={orderedColumns?.length || 1}
            className="px-3 py-2.5 text-[14px] text-gray-500 align-middle border-b border-gray-200"
          >
            No columns available
          </td>
        )}

        {/* MRC Column (Right) */}
        <td
          className="border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-[14px] whitespace-nowrap align-middle tabular-nums"
          style={{ color: "#000000" }}
        >
          {mrc ? formatCurrency(mrc) : "-"}
        </td>

        {/* Edit Button Column */}
        <td className="border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-center whitespace-nowrap align-middle">
          <button
            onClick={() => handleEditClick(row)}
            className="text-gray-600 hover:text-gray-900 transition-colors inline-flex"
            title="View Details"
          >
            <MenuIcon className="w-[18px] h-[18px]" />
          </button>
        </td>

        {/* Verified Checkbox Column */}
        <td className="border-l border-gray-300 border-b border-gray-200 px-3 py-2.5 text-center whitespace-nowrap align-middle">
          <div className="flex items-center justify-center">
            {isVerified(row.verified) ? (
              <input
                type="checkbox"
                checked={true}
                disabled
                readOnly
                className="h-4 w-4 rounded cursor-default text-green-600 focus:ring-green-500 border-green-600 bg-green-50 checked:bg-green-600"
                style={{ accentColor: "#16a34a" }}
              />
            ) : (
              <input
                type="checkbox"
                checked={false}
                disabled
                readOnly
                className="h-4 w-4 rounded cursor-default text-gray-400 focus:ring-gray-400 border-gray-300"
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Render category section with services (3-level hierarchy)
  // Helper function to format address in 2 lines
  const formatTwoLineAddress = (record) => {
    if (!record) return { line1: "-", line2: "" };

    // Get address components (prefer standardized fields)
    const street =
      record.std_street_address ||
      record.street_address ||
      record["Street Address"] ||
      record["A LOC Address"] ||
      "";
    const city =
      record.std_city ||
      record.city ||
      record["City"] ||
      record["A LOC City"] ||
      "";
    const state =
      record.std_state ||
      record.state ||
      record["State"] ||
      record["A LOC State"] ||
      "";
    const zip =
      record.std_zip ||
      record.zip ||
      record["ZIP"] ||
      record["Zip"] ||
      record["A LOC Zip"] ||
      "";

    // Get country from all possible field variations
    const country =
      record.std_country ||
      record.country ||
      record["Country"] ||
      record["A LOC Country"] ||
      "";

    // Build line 1: Street address
    const line1 = street || "-";

    // Build line 2: City, State, Country, ZIP
    const line2Parts = [];
    if (city) line2Parts.push(city);
    if (state) line2Parts.push(state);

    // Add country before ZIP
    if (country && country.trim() !== "") {
      // Format country: if it's a 2-letter code like "US", show it as-is
      // If it's a full name, show it as-is
      const countryValue = country.trim();
      line2Parts.push(countryValue);
    }

    if (zip) line2Parts.push(zip);

    const line2 = line2Parts.length > 0 ? line2Parts.join(", ") : "";

    return { line1, line2 };
  };

  // Helper function to extract Bandwidth value from row data
  const getBandwidthValue = (row, columnName, fieldMappingLookup) => {
    // Strategy 1: Check if columnName directly matches bandwidth (case-insensitive)
    if (columnName && columnName.toLowerCase() === "bandwidth") {
      // First try the columnName as-is
      let value = row[columnName];
      if (value !== null && value !== undefined && value !== "") {
        return value;
      }

      // Try common variations
      const commonVariations = ["bandwidth", "Bandwidth", "BANDWIDTH"];
      for (const variation of commonVariations) {
        value = row[variation];
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
      }
    }

    // Strategy 2: Check if columnName maps to "bandwidth" via fieldMappingLookup
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      // Check if columnName is an input column that maps to "bandwidth"
      const mappedDbColumn = fieldMappingLookup[columnName];
      if (mappedDbColumn && mappedDbColumn.toLowerCase() === "bandwidth") {
        // The columnName is an input column that maps to bandwidth
        // Get value from the input column name
        let value = row[columnName];
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
        // Also try the database column name
        value = row[mappedDbColumn] || row["bandwidth"] || row["Bandwidth"];
        if (value !== null && value !== undefined && value !== "") {
          return value;
        }
      }

      // Strategy 3: Reverse lookup - if columnName is "bandwidth" (database column),
      // find which input column maps to it
      if (columnName && columnName.toLowerCase() === "bandwidth") {
        // Find all input columns that map to "bandwidth"
        const bandwidthMappings = Object.entries(fieldMappingLookup).filter(
          ([inputColumn, inventoryColumn]) =>
            inventoryColumn.toLowerCase() === "bandwidth",
        );

        for (const [inputColumn] of bandwidthMappings) {
          const value = row[inputColumn];
          if (value !== null && value !== undefined && value !== "") {
            return value;
          }
        }
      }
    }

    // Strategy 4: Check if columnName is a known bandwidth input column name
    const knownBandwidthColumns = [
      "Bandwidth",
      "Bandwidth Capacity",
      "bandwidth",
      "BANDWIDTH",
    ];
    if (columnName && knownBandwidthColumns.includes(columnName)) {
      let value = row[columnName];
      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }

    // Strategy 5: Fallback - check common field names in order
    const commonFieldNames = [
      "bandwidth",
      "Bandwidth",
      "Bandwidth Capacity",
      "BANDWIDTH",
    ];
    for (const fieldName of commonFieldNames) {
      const value = row[fieldName];
      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }

    // Return null if not found
    return null;
  };

  // Helper function to identify MRC columns
  const isMRCColumn = (columnName, fieldMappingLookup) => {
    if (!columnName) return false;

    // Direct match: columnName is "mrc" (database column)
    if (columnName.toLowerCase() === "mrc") {
      return true;
    }

    // Known input column names that represent MRC
    const knownMRCColumns = ["MRC", "Total $", "USD Amount"];
    if (knownMRCColumns.includes(columnName)) {
      return true;
    }

    // Check field mapping: if columnName maps to "mrc" via fieldMappingLookup
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      const mappedColumn = fieldMappingLookup[columnName];
      if (mappedColumn && mappedColumn.toLowerCase() === "mrc") {
        return true;
      }
    }

    return false;
  };

  const highlightClass = (row, key) => {
    // Exclude notes and follow_up_date from highlighting
    // These are special fields that can be edited but shouldn't be visually highlighted
    const excludedFields = ["notes", "follow_up_date"];

    // Normalize key for comparison (handle case variations)
    const keyLower = key?.toLowerCase();

    // Check if this field should be excluded from highlighting
    if (excludedFields.some((field) => field.toLowerCase() === keyLower)) {
      return "";
    }

    // Check if row has changes array and if this key is in it
    return row.changes?.includes(key)
      ? "bg-yellow-100 text-yellow-900 font-semibold"
      : "";
  };

  // Render Master Address Page when that tab is active
  if (activeSection === "master-address") {
    return (
      <div className="min-h-screen pt-[155px] bg-brand-background">
        <div className="max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-3">
          <MasterAddressPage
            userRole={userRole}
            tenant={tenant}
            tenantId={tenantId}
            isEmbedded={true}
          />
        </div>
      </div>
    );
  }

  // Site Footprint baseline target and Budget Entry live under Super Admin → Tenant Settings.

  // Upload Monthly Invoice form
  if (activeSection === "upload" && clientAdminSubView === "invoice") {
    const invoiceProviderOptions = [...new Set(providers || [])].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );

    const invoiceBillingOptions = invoiceProvider
      ? billingAccountsByProvider
          .filter((row) => row.provider === invoiceProvider && row.billing_account)
          .sort((a, b) =>
            String(a.billing_account).localeCompare(String(b.billing_account), undefined, {
              numeric: true,
            }),
          )
      : [];

    const handleBillingAccountChange = (accountValue) => {
      setInvoiceBillingAccount(accountValue);
    };
    
    const mrc = parseFloat(invoiceMonthlyMrc);
    const total = parseFloat(invoiceTotalAmount);
    const credit = invoiceCredit.trim() ? parseFloat(invoiceCredit) : null;
    // A credit only belongs to an invoice with MRC of 0 or less.
    const hasMrcAboveZero = !Number.isNaN(mrc) && mrc > 0;
    const taxes = invoiceTaxesAndSurcharges.trim()
      ? parseFloat(invoiceTaxesAndSurcharges)
      : null;

    const isInvoiceFormValid =
      invoiceProvider &&
      invoiceBillingAccount &&
      areGlAllocationsValid(invoiceGlAllocations) &&
      invoiceDate &&
      invoiceNumber.trim() &&
      invoiceMonthlyMrc !== "" &&
      !Number.isNaN(mrc) &&
      invoiceTotalAmount !== "" &&
      !Number.isNaN(total) &&
      invoicePdfFile &&
      (invoiceCredit.trim() === "" || (!Number.isNaN(credit) && credit >= 0)) &&
      (invoiceTaxesAndSurcharges.trim() === "" || !Number.isNaN(taxes));

    const navigateFromInvoiceUpload = () => {
      try {
        const returnRaw = sessionStorage.getItem(INVOICE_UPLOAD_RETURN_KEY);
        if (returnRaw) {
          const ret = JSON.parse(returnRaw);
          if (ret.returnTo === "invoices" && typeof onNavigate === "function") {
            onNavigate("invoices");
            return;
          }
        }
      } catch {
        sessionStorage.removeItem(INVOICE_UPLOAD_RETURN_KEY);
      }
      onNavigate(CLIENT_ADMIN_PAGE.IMPORT);
    };

    const canCloseAccount = canMutateTenantData(userRole);

    const handleInvoiceSubmit = async ({ closeAccount = false } = {}) => {
      if (!isInvoiceFormValid) return;
      if (isFutureInvoiceMonth(invoiceDate)) {
        setToast({
          message: "Invoice date cannot be in a future month.",
          type: "error",
        });
        return;
      }
      if (closeAccount && closingAccountOnUpload) return;
      setInvoiceSubmitting(true);
      if (closeAccount) setClosingAccountOnUpload(true);
      const formData = new FormData();
      formData.append("provider", invoiceProvider);
      formData.append("billing_account", invoiceBillingAccount);
      formData.append("gl_code", invoiceGlAllocations[0]?.gl_code?.trim() || "");
      formData.append(
        "gl_allocations",
        JSON.stringify(serializeGlAllocations(invoiceGlAllocations)),
      );
      formData.append("invoice_date", invoiceDate);
      formData.append("invoice_number", invoiceNumber.trim());
      formData.append("monthly_mrc", String(invoiceMonthlyMrc));
      formData.append("total_invoice_amount", String(invoiceTotalAmount));
      if (invoiceCredit.trim()) {
        formData.append("credit_amount", String(invoiceCredit));
      }
      if (invoiceTaxesAndSurcharges.trim()) {
        formData.append("taxes_and_surcharges", String(invoiceTaxesAndSurcharges));
      }
      if (invoiceDueDate) {
        formData.append("due_date", invoiceDueDate);
      }
      if (invoiceNote.trim()) {
        formData.append("note", invoiceNote.trim());
      }
      formData.append("file", invoicePdfFile);
      const providerForClose = invoiceProvider;
      const accountForClose = invoiceBillingAccount;
      try {
        await apiService.uploadMonthlyInvoice(formData);

        let closedOk = false;
        let closeFailedMessage = null;
        if (closeAccount) {
          try {
            await apiService.closeInvoiceBillingAccount(
              providerForClose,
              accountForClose,
            );
            closedOk = true;
          } catch (closeErr) {
            closeFailedMessage =
              closeErr.message || "Invoice saved, but closing the account failed.";
          }
        }

        let returnToInvoices = false;
        try {
          const returnRaw = sessionStorage.getItem(INVOICE_UPLOAD_RETURN_KEY);
          if (returnRaw) {
            const ret = JSON.parse(returnRaw);
            returnToInvoices = ret.returnTo === "invoices";
          }
        } catch {
          sessionStorage.removeItem(INVOICE_UPLOAD_RETURN_KEY);
        }

        const successMessage = closedOk
          ? "Invoice saved and account closed. This was treated as the last invoice."
          : closeFailedMessage
            ? closeFailedMessage
            : "Your invoice was saved successfully.";
        const toastType = closeFailedMessage ? "warning" : "success";

        if (returnToInvoices && typeof onNavigate === "function") {
          sessionStorage.setItem(
            INVOICE_UPLOAD_SUCCESS_TOAST_KEY,
            successMessage,
          );
          if (typeof refreshDropdownValues === "function") {
            refreshDropdownValues();
          }
          setInvoicePendingSaveAndClose(false);
          onNavigate("invoices");
          return;
        }

        setToast({
          message: successMessage,
          type: toastType,
        });
        setInvoiceProvider("");
        setInvoiceBillingAccount("");
        setInvoiceGlCode("");
        setInvoiceGlSplit(false);
        setInvoiceGlAllocations([newGlAllocation("", "100")]);
        setInvoiceDate("");
        setInvoiceNumber("");
        setInvoiceMonthlyMrc("");
        setInvoiceTotalAmount("");
        setInvoiceCredit("");
        setInvoiceTaxesAndSurcharges("");
        setInvoiceDueDate("");
        setInvoiceNote("");
        setInvoicePdfFile(null);
        setInvoicePdfFileName("");
        setInvoicePendingSaveAndClose(false);
        if (typeof refreshDropdownValues === "function") {
          refreshDropdownValues();
        }
      } catch (err) {
        setToast({
          message: err.message || "Failed to save invoice.",
          type: "error",
        });
      } finally {
        setInvoiceSubmitting(false);
        setClosingAccountOnUpload(false);
      }
    };

    return (
      <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
        <div className="fixed top-4 right-4 z-[110]">
          <Toast
            message={toast.message}
            title={toast.title}
            type={toast.type}
            onClose={() => setToast({ message: null, type: "success" })}
          />
        </div>
        <div className="w-full px-5 py-2 pb-14">
            {invoiceEntryMode === "choose" ? (
              <InvoiceUploadMethodChooser
                onChooseExtract={() => setInvoiceEntryMode("extract")}
                onChooseManual={() => setInvoiceEntryMode("manual")}
                onOpenInvoices={() => {
                  if (typeof onNavigate === "function") onNavigate("invoices");
                }}
                invoiceMonth={waitingInvoiceMonth}
                waitingRows={waitingInvoiceRows}
                receivedCount={invoiceProgress.received}
                expectedCount={invoiceProgress.expected}
                closedThroughLabel={closedThroughLabel}
                onUploadPdf={(inv) => {
                  if (inv?.provider) setInvoiceProvider(String(inv.provider));
                  if (inv?.billing_account) {
                    setInvoiceBillingAccount(
                      normalizeBillingAccount(inv.billing_account),
                    );
                  }
                  const gl = formatGlAllocationsLabel(
                    inv?.gl_allocations,
                    inv?.gl_code || "",
                  );
                  if (gl) {
                    setInvoiceGlCode(String(gl));
                    setInvoiceGlAllocations([newGlAllocation(String(gl), "100")]);
                    setInvoiceGlSplit(false);
                  }
                  if (waitingInvoiceMonth) {
                    setInvoiceDate(`${waitingInvoiceMonth}-01`);
                  }
                  setInvoiceEntryMode("manual");
                }}
              />
            ) : (
            <>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
            Client Administration
          </p>
          <div className="mt-1 mb-5 flex items-center justify-between gap-3">
              <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                Upload Monthly Invoice
              </h1>
              <button
                type="button"
                onClick={() => setInvoiceEntryMode("choose")}
                className="text-[13px] font-medium text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
              >
                ← Back
              </button>
            </div>
          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden mb-6">
            {invoiceEntryMode === "extract" ? (
              <InvoiceExtractionUpload
                onBack={() => setInvoiceEntryMode("choose")}
                onSwitchToManual={() => setInvoiceEntryMode("manual")}
                setToast={setToast}
                providers={providers}
                billingAccountsByProvider={billingAccountsByProvider}
                glCodes={glCodes}
                canCloseAccount={canMutateTenantData(userRole)}
                refreshDropdownValues={refreshDropdownValues}
                onSaved={() => {
                  try {
                    const returnRaw = sessionStorage.getItem(
                      INVOICE_UPLOAD_RETURN_KEY,
                    );
                    if (returnRaw) {
                      const ret = JSON.parse(returnRaw);
                      if (
                        ret.returnTo === "invoices" &&
                        typeof onNavigate === "function"
                      ) {
                        sessionStorage.setItem(
                          INVOICE_UPLOAD_SUCCESS_TOAST_KEY,
                          "Your invoice was saved successfully.",
                        );
                        if (typeof refreshDropdownValues === "function") {
                          refreshDropdownValues();
                        }
                        onNavigate("invoices");
                        return;
                      }
                    }
                  } catch {
                    sessionStorage.removeItem(INVOICE_UPLOAD_RETURN_KEY);
                  }
                  if (typeof refreshDropdownValues === "function") {
                    refreshDropdownValues();
                  }
                  setInvoiceEntryMode("choose");
                }}
              />
            ) : (
            <div className="p-6 space-y-6">
              {/* <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setInvoiceEntryMode("choose")}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  ← Back to upload options
                </button>
              </div> */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">
                  Account
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                      value={invoiceProvider}
                      onChange={(e) => {
                        setInvoiceProvider(e.target.value);
                        setInvoiceBillingAccount("");
                      }}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400"
                    >
                      <option value="">Select a provider</option>
                      {invoiceProviderOptions.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </CustomSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing account <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                      value={invoiceBillingAccount}
                      onChange={(e) => handleBillingAccountChange(e.target.value)}
                      disabled={!invoiceProvider}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {invoiceProvider ? "Select a billing account" : "Select a provider first"}
                      </option>
                      {invoiceBillingOptions.map((account) => (
                        <option
                          key={`${account.provider}-${account.billing_account}`}
                          value={normalizeBillingAccount(account.billing_account)}
                        >
                          {account.show_as || account.billing_account}
                        </option>
                      ))}
                    </CustomSelect>
                  </div>
                </div>
                <InvoiceGlAllocationEditor
                  glCodes={glCodes}
                  allocations={invoiceGlAllocations}
                  onAllocationsChange={(next) => {
                    setInvoiceGlAllocations(next);
                    setInvoiceGlCode(String(next[0]?.gl_code || ""));
                  }}
                  splitEnabled={invoiceGlSplit}
                  onSplitChange={setInvoiceGlSplit}
                  totalAmount={invoiceTotalAmount}
                  disabled={invoiceSubmitting}
                />
              </div>

              <div className="h-px bg-stone-200 my-4" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">
                  Invoice details
                </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice date <span className="text-red-500">*</span>
                  </label>
                  <YmdDateInput
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    max={getCurrentInvoiceMonthEndDate()}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Sets which month this invoice lands in.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Enter invoice number"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly MRC <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={invoiceMonthlyMrc}
                    onChange={(e) => {
                      const next = e.target.value;
                      setInvoiceMonthlyMrc(next);
                      const parsed = parseFloat(next);
                      if (!Number.isNaN(parsed) && parsed > 0) {
                        setInvoiceCredit("0");
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-gray-50 disabled:cursor-not-allowed 
                    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Recurring monthly charges only, no one time charges or tax.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total invoice amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={invoiceTotalAmount}
                    onChange={(e) => setInvoiceTotalAmount(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-gray-50 disabled:cursor-not-allowed 
                    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Full invoice including one time charges and tax.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit (optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  onWheel={(e) => e.target.blur()}
                  value={hasMrcAboveZero ? "0" : invoiceCredit}
                  onChange={(e) => setInvoiceCredit(e.target.value)}
                  disabled={hasMrcAboveZero}
                  placeholder="0.00"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-gray-50 disabled:cursor-not-allowed
                  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Standing credit for this account. Only available when Monthly MRC is $0 or less.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taxes and Surcharges (optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  onWheel={(e) => e.target.blur()}
                  value={invoiceTaxesAndSurcharges}
                  onChange={(e) => setInvoiceTaxesAndSurcharges(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400
                  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date (optional)
                  </label>
                  <YmdDateInput
                    value={invoiceDueDate}
                    onChange={(e) => setInvoiceDueDate(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400"
                  />
                </div>
              </div>
              </div>
              <div className="h-px bg-stone-200 my-4" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (optional)
                </label>
                <textarea
                  value={invoiceNote}
                  onChange={(e) => setInvoiceNote(e.target.value)}
                  rows={5}
                  maxLength={50000}
                  placeholder="Add context for this invoice."
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {invoiceNote.length.toLocaleString()} / 50,000 characters
                </p>
              </div>
              <div className="h-px bg-stone-200 my-4" />
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">
                  Document
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice PDF <span className="text-red-500">*</span>
                </label>
                <div className="relative flex flex-col items-center justify-center px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-gray-400 hover:bg-gray-100 transition-colors cursor-pointer group">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 rounded-lg"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setInvoicePdfFile(f);
                        setInvoicePdfFileName(f.name);
                      }
                    }}
                  />
                  <UploadCloudIcon className="w-12 h-12 text-gray-400 group-hover:text-gray-500 mb-2" />
                  {invoicePdfFileName ? (
                    <p className="text-base font-medium text-gray-900">{invoicePdfFileName}</p>
                  ) : (
                    <p className="text-base font-medium text-gray-700">Click to upload PDF</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setInvoiceEntryMode("choose")}
                  className="px-4 py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                {canCloseAccount ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isInvoiceFormValid || invoiceSubmitting) return;
                      if (isFutureInvoiceMonth(invoiceDate)) {
                        setToast({
                          message: "Invoice date cannot be in a future month.",
                          type: "error",
                        });
                        return;
                      }
                      setInvoicePendingSaveAndClose(true);
                    }}
                    disabled={!isInvoiceFormValid || invoiceSubmitting}
                    className="inline-flex items-center justify-center px-5 py-2.5 border border-stone-700 text-base font-medium rounded-md shadow-sm text-stone-800 bg-white hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                    title="Save this invoice and mark the billing account closed (last invoice)"
                  >
                    Save & Close Account
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleInvoiceSubmit()}
                  disabled={!isInvoiceFormValid || invoiceSubmitting}
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--accent,#a51c1c)] disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {invoiceSubmitting && !closingAccountOnUpload
                    ? "Saving..."
                    : "Save Invoice"}
                </button>
              </div>
            </div>
            )}
          </div>
            </>
            )}
        </div>

        {canCloseAccount && invoicePendingSaveAndClose ? (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
            role="presentation"
            onClick={() => {
              if (invoiceSubmitting || closingAccountOnUpload) return;
              setInvoicePendingSaveAndClose(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="upload-close-account-title"
              className="bg-white rounded-lg max-w-md w-full shadow-xl border-t-4 border-stone-700 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 pt-4 pb-2 border-b border-gray-100 bg-stone-50">
                <h2
                  id="upload-close-account-title"
                  className="text-lg font-bold text-stone-900"
                >
                  Save & close account
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Saves this invoice, then closes the billing account as the last
                  invoice. This month and prior months keep their records. Future
                  months will not list or count this account.
                </p>
              </div>
              <div className="px-5 py-4">
                <div className="bg-gray-50 rounded-md border border-gray-200 p-3 space-y-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Provider: </span>
                    <span className="text-gray-800">{invoiceProvider}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">
                      Billing account:{" "}
                    </span>
                    <span className="text-gray-800">{invoiceBillingAccount}</span>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex gap-2 justify-end border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setInvoicePendingSaveAndClose(false)}
                  disabled={invoiceSubmitting || closingAccountOnUpload}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleInvoiceSubmit({ closeAccount: true })}
                  disabled={invoiceSubmitting || closingAccountOnUpload}
                  className="inline-flex items-center justify-center px-4 py-2 border-0 text-sm font-medium rounded-md text-white bg-stone-800 hover:bg-stone-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                >
                  {invoiceSubmitting || closingAccountOnUpload
                    ? "Saving & closing..."
                    : "Save & close account"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Render import history / replace wizard (primary import inventory flow)
  if (clientAdminSubView === "import" && importView === "history") {
    return (
      <ImportHistoryPanel
        canMutate={canMutateTenantData(userRole)}
        onImportNew={() => {
          setImportView("wizard");
          setImportStep("upload");
        }}
        onOpenMappings={() => setImportView("mappings")}
      />
    );
  }

  if (clientAdminSubView === "import" && importView === "mappings") {
    return (
      <ImportFieldMappingsPanel onBack={() => setImportView("history")} />
    );
  }

  if (clientAdminSubView === "import" && importView === "wizard") {
    return (
      <ImportReplaceWizard
        tenantId={selectedTenantId}
        onBack={() => setImportView("history")}
        onPreviewReady={async ({
          comparison,
          importResult: stagedResult,
          importId,
          provider: nextProvider,
          category: nextCategory,
          fileName,
        }) => {
          setProvider(nextProvider);
          setCategory(nextCategory);
          setReplacePreviewComparison(comparison || null);
          setReplaceFileName(fileName || "");
          setImportResult(stagedResult);
          setIsSuccess(true);
          if (importId) {
            localStorage.setItem("lastImportId", String(importId));
            localStorage.setItem("lastImportProvider", nextProvider);
            localStorage.setItem("lastImportCategory", nextCategory);
            setCurrentImportId(String(importId));
          }
          setImportView("review");
          setImportStep("preview");
          setIsLoadingPreview(true);
          try {
            if (importId) {
              await loadPage(String(importId), 1, pageSize);
            }
          } catch (previewError) {
            console.error("Failed to load preview:", previewError);
            setToast({
              message:
                previewError.message ||
                "Staged import succeeded but preview records could not be loaded.",
              type: "error",
            });
          } finally {
            setIsLoadingPreview(false);
          }
        }}
        onComplete={() => {
          setReplacePreviewComparison(null);
          setReplaceFileName("");
          setImportView("history");
          setImportStep("upload");
          setToast({
            message:
              "Inventory replaced successfully. See import history for details.",
            type: "success",
          });
        }}
      />
    );
  }

  // Legacy upload UI kept for non-wizard paths; not used by the replace flow above
  if (importStep === "upload" && clientAdminSubView === "import") {
    return (
      <div className="min-h-screen pt-[155px] bg-brand-background">
        <div className="max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-6">
          <div className="bg-white rounded-md shadow-sm border-0 overflow-hidden mb-6">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center gap-4">
              <button
                type="button"
                onClick={() => setImportView("history")}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to import history
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Import New Inventory
              </h2>
            </div>
            <div className="p-6 relative">
              {/* Initial load / dropdown loading: SpinnerCustom (not the file-upload overlay) */}
              {(isInitialLoad || dropdownLoading) && !showLoader && !isUploading && (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-40 flex flex-col items-center justify-center rounded-lg">
                  <SpinnerCustom />
                </div>
              )}
              <Loading
                show={showLoader}
                message="Processing your file…"
                subMessage="Please wait while we import your data"
                icon={<UploadCloudIcon className="w-6 h-6 text-[var(--accent,#a51c1c)]" />}
                className="rounded-lg"
              />

              {validationError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start">
                    <AlertTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-2 text-sm text-red-700">
                      {validationError}
                    </p>
                  </div>
                </div>
              )}

              {apiError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-semibold text-red-800 mb-1">
                        Import Failed
                      </h3>
                      <p className="text-sm text-red-700 leading-relaxed">
                        {apiError}
                      </p>
                      {apiError.includes("Missing required columns") && (
                        <p className="mt-2 text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                          💡 Tip: Ensure all mapped columns exist in your file.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full bg-white border border-gray-300 rounded-md py-2.5 px-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] transition-colors"
                    >
                      <option value="" disabled>
                        Select a Category
                      </option>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </CustomSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider <span className="text-red-500">*</span>
                    </label>
                    <CustomSelect
                      value={provider}
                      onChange={(e) => setProvider(e.target.value)}
                      disabled={!category}
                      className="w-full bg-white border border-gray-300 rounded-md py-2.5 px-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500 transition-colors"
                    >
                      <option value="" disabled>
                        {category
                          ? "Select a provider"
                          : "Select a category first"}
                      </option>
                      {availableProviders.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </CustomSelect>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {category === "Custom" && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-800">
                        Download the{" "}
                        <button
                          onClick={handleDownloadTemplate}
                          type="button"
                          className="text-blue-600 hover:text-blue-800 underline font-medium bg-transparent border-none p-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                        >
                          template
                        </button>{" "}
                        to see the required column format (please do not change
                        the system-generated column headers).
                      </p>
                    </div>
                  )}

                  {provider === "Lumen" && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                      <p className="text-xs font-semibold text-yellow-900 mb-2">
                        Important: Lumen File Preparation
                      </p>
                      <ul className="text-xs text-yellow-800 space-y-1 list-disc list-inside">
                        <li>
                          Please remove the first two header rows manually from
                          the Excel file before uploading
                        </li>
                        <li>
                          Remove the unnecessary Account types from the Excel
                          file
                        </li>
                      </ul>
                    </div>
                  )}

                  <div className="relative">
                    <div className="flex flex-col items-center justify-center px-6 py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-gray-400 hover:bg-gray-100 transition-colors cursor-pointer group">
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        onChange={handleFileChange}
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      />
                      <UploadCloudIcon className="w-12 h-12 text-gray-400 group-hover:text-gray-500 mb-2 transition-colors" />
                      <div className="text-center">
                        {fileName ? (
                          <div className="space-y-1">
                            <p className="text-base font-medium text-gray-900">
                              {fileName}
                            </p>
                            <p className="text-sm text-gray-500">
                              Click to change file
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <h5 className="text-base font-medium text-gray-700 mb-1">
                              <span className="text-blue-600 group-hover:text-blue-700">
                                Click to Upload File
                              </span>{" "}
                              or drag and drop
                            </h5>
                            <span className="text-sm text-gray-500">
                              CSV, XLS, XLSX (max 10MB)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={handleImportAndPreview}
                  disabled={
                    !category ||
                    !provider ||
                    !selectedFile ||
                    !!validationError ||
                    isUploading ||
                    loading ||
                    isLoadingPreview
                  }
                  className={`inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300 transition-colors`}
                >
                  {(isUploading || loading || isLoadingPreview) ? (
                    <Spinner className="w-4 h-4 text-gray-600" />
                  ) : (
                    "Upload & Preview"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (importStep === "summary") {
    const summary = importResult?.data?.summary || {};
    const importedCount =
      summary.imported_records || importResult?.data?.imported_count || 0;
    const addressLinkSummary = summary.address_link_summary || null;
    if (addressLinkSummary) {
      try {
        sessionStorage.setItem(
          "masterAddressLinkSummary",
          JSON.stringify(addressLinkSummary),
        );
      } catch {
        /* ignore */
      }
    }

    // Check if this is Nitel Data import
    const isNitelData =
      provider?.toLowerCase() === "nitel" && category?.toLowerCase() === "data";

    return (
      <div className="min-h-screen pt-[155px] bg-brand-background">
        <div className="max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-3">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Import Summary
            </h1>
            <p className="text-sm text-gray-600">
              Your file has been processed successfully
            </p>
          </div>

          <div className="bg-white rounded-md shadow-sm border-0 overflow-hidden mb-6 p-6">
            {/* Success Message */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <CheckCircleIcon className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-green-800 mb-1">
                    Import Successful
                  </h3>
                  <p className="text-base text-green-700 font-medium">
                    {importedCount} record{importedCount !== 1 ? "s" : ""}{" "}
                    imported successfully
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Details */}
            {importResult?.data?.summary && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Import Details
                </h4>
                <div
                  className={`grid grid-cols-2 ${
                    isNitelData ? "md:grid-cols-5" : "md:grid-cols-4"
                  } gap-4`}
                >
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">
                      Total Records
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {summary.total_records || 0}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">
                      Imported Records
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {summary.imported_records || 0}
                    </div>
                  </div>
                  {isNitelData && summary.in_service_records !== undefined && (
                    <div className="bg-blue-50 p-3 rounded border border-blue-200">
                      <div className="text-xs text-blue-700 font-medium mb-1">
                        In Service Records
                      </div>
                      <div className="text-lg font-semibold text-blue-900">
                        {summary.in_service_records || 0}
                      </div>
                    </div>
                  )}
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">
                      Unique Addresses
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {summary.unique_addresses || 0}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-600 mb-1">
                      {isNitelData && summary.in_service_mrc !== undefined
                        ? "In Service MRC"
                        : "Total MRC"}
                    </div>
                    <div className="text-lg font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(
                        isNitelData && summary.in_service_mrc !== undefined
                          ? summary.in_service_mrc || 0
                          : summary.total_mrc || 0,
                      )}
                    </div>
                  </div>
                </div>
                {addressLinkSummary && (
                  <div className="mt-4 rounded-md border border-gray-200 bg-white px-4 py-3">
                    <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Master address linking
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs">Exact match</div>
                        <div className="font-semibold text-gray-900">
                          {addressLinkSummary.exact_match || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">
                          Previously linked
                        </div>
                        <div className="font-semibold text-gray-900">
                          {addressLinkSummary.previously_linked || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">
                          Waiting confirmation
                        </div>
                        <div className="font-semibold text-brand-red">
                          {addressLinkSummary.waiting_confirmation || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">No match</div>
                        <div className="font-semibold text-gray-900">
                          {addressLinkSummary.no_match || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={resetImport}
                className="px-5 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Import Another File
              </button>
              <button
                onClick={handlePreviewClick}
                disabled={isLoadingPreview}
                className={`inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${tenant.primaryBgColor} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-opacity`}
              >
                {isLoadingPreview ? (
                  <span className="inline-flex items-center">
                    <Spinner className="w-4 h-4 text-white" />
                  </span>
                ) : (
                  "View Import Records"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Replace flow — step 2: comparison cards + record groups only
  if (importStep === "preview" && replacePreviewComparison) {
    const previewCurrent = replacePreviewComparison.current_count ?? 0;
    const previewAfter = replacePreviewComparison.file_count ?? 0;
    const previewNet =
      replacePreviewComparison.net_change ?? previewAfter - previewCurrent;
    const previewNetLabel =
      previewNet > 0
        ? `+${previewNet.toLocaleString()}`
        : previewNet.toLocaleString();
    const goBackToUpload = () => {
      setReplacePreviewComparison(null);
      setReplaceFileName("");
      setImportView("wizard");
      setImportStep("upload");
    };

    return (
      <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <div className="fixed top-4 right-4 z-[110]">
            <Toast
              message={toast.message}
              title={toast.title}
              type={toast.type}
              onClose={() => setToast({ message: null, type: "success" })}
            />
          </div>

          <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
                Client Administration
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={goBackToUpload}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                  aria-label="Back"
                >
                  ←
                </button>
                <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                  Preview import
                </h1>
                <p className="text-[14px] text-[var(--muted,#6b6a66)]">
                  {provider} · {category}
                  {replaceFileName ? ` · ${replaceFileName}` : ""}
                </p>
              </div>
            </div>
            <ImportReplaceStepper step={2} />
          </div>

          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-5 py-4 mb-4">
            <div className="flex flex-wrap items-stretch gap-0">
              <div className="flex-1 min-w-[160px] pr-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Current inventory
                </div>
                <div className="mt-2 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {previewCurrent.toLocaleString()}{" "}
                  <span className="text-[15px] font-medium text-[var(--muted,#6b6a66)]">
                    services
                  </span>
                </div>
              </div>

              <div className="hidden sm:flex items-center px-2 text-[var(--muted,#6b6a66)]" aria-hidden>
                →
              </div>

              <div className="flex-1 min-w-[160px] px-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  After import
                </div>
                <div className="mt-2 text-[22px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {previewAfter.toLocaleString()}{" "}
                  <span className="text-[15px] font-medium text-[var(--muted,#6b6a66)]">
                    services
                  </span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] pl-5 sm:border-l border-[var(--line,#e4e2dc)]">
                <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Net change
                </div>
                <div className="mt-2 text-[22px] font-semibold tabular-nums text-[var(--accent,#a51c1c)]">
                  {previewNetLabel}{" "}
                  <span className="text-[15px] font-medium">services</span>
                </div>
                <div className="mt-2 pt-2 border-t border-dashed border-[var(--line,#e4e2dc)] text-[12px] text-[var(--muted,#6b6a66)] space-y-0.5">
                  <div>
                    {(replacePreviewComparison.counts?.added ?? 0).toLocaleString()}{" "}
                    new ·{" "}
                    {(replacePreviewComparison.counts?.leaving ?? 0).toLocaleString()}{" "}
                    leaving ·{" "}
                    {(replacePreviewComparison.counts?.changed ?? 0).toLocaleString()}{" "}
                    changed
                  </div>
                  <div>
                    {(replacePreviewComparison.counts?.unchanged ?? 0).toLocaleString()}{" "}
                    unchanged
                    {(replacePreviewComparison.counts?.skipped ?? 0) > 0
                      ? ` · ${(replacePreviewComparison.counts.skipped).toLocaleString()} not imported`
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ReplaceComparisonGroupFilter
            key={`${replacePreviewComparison.file_count}-${replacePreviewComparison.current_count}`}
            comparison={replacePreviewComparison}
          />

          <p className="text-[12px] text-[var(--muted,#6b6a66)] mt-4">
            Nothing changes in inventory until you click Import. The current{" "}
            {provider} {category} set is kept in Import history so this import
            can be rolled back. Other providers and categories are not touched.
          </p>

          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={goBackToUpload}
              disabled={isLoadingPreview}
              className="px-5 py-2 border border-[var(--line-strong,#d5d3cc)] text-[13px] font-medium rounded-md text-[var(--ink,#1c1c1a)] bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setImportStep("import")}
              disabled={isLoadingPreview}
              className="flex items-center justify-center gap-2 min-h-[42px] px-4 py-2.5 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] text-white text-[14px] font-semibold whitespace-nowrap disabled:opacity-50"
            >
              Ready to import {previewAfter.toLocaleString()} records
            </button>
          </div>

          <Loading
            show={isLoadingPreview}
            overlayMode="fixed"
            message="Preparing preview…"
            subMessage="Comparing against current inventory and loading records for review."
          />
        </div>
      </div>
    );
  }

  // Replace flow step 3 (import) OR legacy merge preview
  if (
    importStep === "import" ||
    (importStep === "preview" && !replacePreviewComparison)
  ) {
    const summary = importResult?.data?.summary || {};

    // Check if this is Nitel Data import
    const isNitelData =
      provider?.toLowerCase() === "nitel" && category?.toLowerCase() === "data";

    return (
      <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
        <div className="w-full px-5 py-2 pb-14">
          <div className="fixed top-4 right-4 z-[110]">
            <Toast
              message={toast.message}
              title={toast.title}
              type={toast.type}
              onClose={() => setToast({ message: null, type: "success" })}
            />
          </div>
          <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
                Client Administration
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {replacePreviewComparison && importStep === "import" ? (
                  <button
                    type="button"
                    onClick={() => setImportStep("preview")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                    aria-label="Back"
                  >
                    ←
                  </button>
                ) : null}
                <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                  {replacePreviewComparison ? "Import" : "Import new inventory"}
                </h1>
                {(provider || category || replaceFileName) ? (
                  <p className="text-[14px] text-[var(--muted,#6b6a66)]">
                    {[provider, category, replaceFileName]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                ) : null}
              </div>
            </div>
            {replacePreviewComparison && importStep === "import" ? (
              <ImportReplaceStepper step={3} />
            ) : null}
          </div>

          {/* Summary metrics + status filter + standardize — one row */}
          {(importResult?.data?.summary || currentImportId) && (
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden mb-4 px-6 py-3.5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 text-[13px] flex-wrap">
                  {importResult?.data?.summary && (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[var(--muted,#6b6a66)]">Total Records:</span>
                        <span className="font-semibold text-[var(--ink,#1c1c1a)]">
                          {summary.total_records || 0}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:bg-[var(--line-strong,#d5d3cc)] before:rounded-full">
                        <span className="text-[var(--muted,#6b6a66)]">Imported Records:</span>
                        <span className="font-semibold text-[var(--ink,#1c1c1a)]">
                          {summary.imported_records || 0}
                        </span>
                      </div>
                      {isNitelData &&
                        summary.in_service_records !== undefined && (
                          <div className="flex items-center gap-1.5 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:bg-[var(--line-strong,#d5d3cc)] before:rounded-full">
                            <span className="text-[var(--muted,#6b6a66)]">In Service:</span>
                            <span className="font-semibold text-[var(--ink,#1c1c1a)]">
                              {summary.in_service_records || 0}
                            </span>
                          </div>
                        )}
                      <div className="flex items-center gap-1.5 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:bg-[var(--line-strong,#d5d3cc)] before:rounded-full">
                        <span className="text-[var(--muted,#6b6a66)]">Unique Addresses:</span>
                        <span className="font-semibold text-[var(--ink,#1c1c1a)]">
                          {summary.unique_addresses || 0}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:bg-[var(--line-strong,#d5d3cc)] before:rounded-full">
                        <span className="text-[var(--muted,#6b6a66)]">
                          {isNitelData && summary.in_service_mrc !== undefined
                            ? "In Service MRC:"
                            : "Total MRC:"}
                        </span>
                        <span className="font-semibold text-[var(--ink,#1c1c1a)] tabular-nums">
                          {formatCurrency(
                            isNitelData && summary.in_service_mrc !== undefined
                              ? summary.in_service_mrc || 0
                              : summary.total_mrc || 0,
                          )}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center gap-2 relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:bg-[var(--line-strong,#d5d3cc)] before:rounded-full">
                    <span className="text-[var(--muted,#6b6a66)] font-medium">
                      Status Filter:
                    </span>
                    <CustomSelect
                      value={statusFilter}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (VALID_IMPORT_STATUSES.includes(newValue)) {
                          setStatusFilter(newValue);
                        } else {
                          console.warn(
                            `Invalid status filter value: ${newValue}, defaulting to "all"`,
                          );
                          setStatusFilter("all");
                        }
                      }}
                      className="px-3 py-1.5 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]"
                    >
                      <option value="all">All</option>
                      <option value="new">New</option>
                      <option value="updated">Updated</option>
                      <option value="same">Same</option>
                    </CustomSelect>
                  </div>
                </div>
                {/* <div className="flex items-center gap-3">
                  {isStandardizing && (
                    <div className="flex items-center px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                      <span className="text-yellow-800 whitespace-nowrap">
                        Standardizing...
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleStandardizeAllAddresses}
                    disabled={
                      isStandardizing ||
                      loading ||
                      !currentImportId ||
                      (statusFilter !== "all" &&
                        (groupedInventory.length === 0 || totalRecords === 0))
                    }
                    className="px-4 py-2 bg-[var(--good,#1f6b34)] text-white text-[13px] font-medium rounded-md hover:opacity-90 whitespace-nowrap disabled:bg-[#9a9a96] disabled:cursor-not-allowed"
                  >
                    Standardize all addresses
                  </button>
                </div> */}
              </div>
            </div>
          )}

          {/* Address Table */}
          <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden mb-6">
            <div className="overflow-x-auto w-full">
              <table className="w-full text-[15px] text-left text-[var(--ink,#1c1c1a)]">
                <thead className="bg-[var(--sunken,#eeece7)]">
                  <tr>
                    <th className="px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)]">
                      <div className="flex items-center gap-2">
                        <span>ADDRESS</span>
                        {renderSortButtons()}
                      </div>
                    </th>
                    <th className="px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)] text-right">
                      SERVICES
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingPage ? (
                    <tr>
                      <td
                        colSpan={100}
                        className="px-5 py-8 text-center text-gray-500 text-[15px]"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <SpinnerCustom />
                        </div>
                      </td>
                    </tr>
                  ) : !groupedInventory || groupedInventory.length === 0 ? (
                    <tr>
                      <td colSpan={100} className="px-5 py-8 text-center">
                        <p className="text-[15px] text-gray-500">
                          No records found.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    groupedInventory.map((group) => {
                      const addressDisplay = formatAddressDisplay(group);
                      const expanded = isExpanded(group.addressKey);

                      return (
                        <React.Fragment key={group.addressKey}>
                          {/* Main Address Row */}
                          <tr
                            className="bg-white hover:bg-[var(--sunken,#eeece7)] cursor-pointer transition-colors"
                            onClick={() => toggleAddress(group.addressKey)}
                          >
                            <td
                              colSpan={100}
                              className="px-5 py-3.5 border-b border-[var(--line,#e4e2dc)]"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <LocationIcon className="w-[18px] h-[18px] text-gray-600" />
                                  <span className="text-[15px] text-gray-900 font-medium">
                                    {addressDisplay}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-[15px]">
                                  <div className="font-normal text-gray-700">
                                    {group.totalCount}{" "}
                                    {group.totalCount === 1
                                      ? "service"
                                      : "services"}
                                  </div>
                                  <div className="font-bold text-gray-900 ml-2 tabular-nums">
                                    {formatCurrency(group.totalMRC || 0)}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Rows - Show all service items */}
                          {expanded && (
                            <tr>
                              <td colSpan={100} className="p-0">
                                <div className="border-t border-gray-200 p-0">
                                  <div className="overflow-x-auto w-full">
                                    <table
                                      className="w-full mb-0.5"
                                      style={
                                        hasWorksWithCircuitIdColumn
                                          ? undefined
                                          : { minWidth: "max-content" }
                                      }
                                    >
                                      {renderTableHeader()}
                                      <tbody>
                                        {group.items.map((item, idx) =>
                                          renderItemRow(item, idx),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {(totalPages > 0 ||
                (totalRecords !== undefined && totalRecords > 0)) && (
                <div className="py-3 px-5 border-t border-[var(--line,#e4e2dc)] bg-white">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2">
                      <label className="text-[14px] text-gray-700">Show:</label>
                      <CustomSelect
                        value={pageSize || 20}
                        onChange={(e) =>
                          handlePageSizeChange(parseInt(e.target.value))
                        }
                        className="border border-gray-300 rounded-md py-1.5 px-2.5 text-[14px] text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </CustomSelect>
                      <span className="text-[14px] text-gray-700">
                        per page
                      </span>
                    </div>

                    {/* Pagination Info and Controls */}
                    {totalPages > 1 ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1 || isLoadingPage}
                          className="px-3 py-1.5 text-[14px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          ««
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1 || isLoadingPage}
                          className="px-3 py-1.5 text-[14px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Prev
                        </button>
                        <div className="flex items-center gap-1">
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              const isCurrentPage = currentPage === pageNum;

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  disabled={isLoadingPage}
                                  className={`px-3 py-1.5 text-[14px] font-medium rounded-md ${
                                    isCurrentPage
                                      ? "bg-[var(--accent,#a51c1c)] text-white"
                                      : "bg-white text-[var(--ink,#1c1c1a)] border border-[var(--line-strong,#d5d3cc)] hover:bg-[var(--sunken,#eeece7)]"
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            },
                          )}
                        </div>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages || isLoadingPage}
                          className="px-3 py-1.5 text-[14px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages || isLoadingPage}
                          className="px-3 py-1.5 text-[14px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          »»
                        </button>
                      </div>
                    ) : (
                      <div></div>
                    )}

                    {/* Page Info - Show address groups being paginated and total services */}
                    <div className="text-[14px] text-gray-700">
                      {totalPages > 0 && totalRecords !== undefined ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <div>
                            Showing{" "}
                            <span className="font-medium">
                              {(
                                (currentPage - 1) * pageSize +
                                1
                              ).toLocaleString()}
                            </span>
                            {" - "}
                            <span className="font-medium">
                              {Math.min(
                                currentPage * pageSize,
                                totalRecords,
                              ).toLocaleString()}
                            </span>
                            {" of "}
                            <span className="font-medium">
                              {totalRecords.toLocaleString()}
                            </span>{" "}
                            {totalRecords === 1 ? "address" : "addresses"}
                          </div>
                          {summary && summary.imported_records && (
                            <div className="text-gray-600">
                              <span className="font-medium">
                                {summary.imported_records.toLocaleString()}
                              </span>{" "}
                              total{" "}
                              {summary.imported_records === 1
                                ? "service"
                                : "services"}{" "}
                              imported
                            </div>
                          )}
                        </div>
                      ) : totalPages > 0 ? (
                        <>
                          Page{" "}
                          <span className="font-medium">{currentPage}</span> of{" "}
                          <span className="font-medium">{totalPages}</span>
                        </>
                      ) : (
                        <span>No addresses</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Cancel and Confirm Merge / Replace - at bottom of tab */}
              <div className="px-5 py-4 border-t border-[var(--line,#e4e2dc)] bg-white">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={resetImport}
                    className="px-5 py-2 border border-[var(--line-strong,#d5d3cc)] text-[13px] font-medium rounded-md text-[var(--ink,#1c1c1a)] bg-white hover:bg-[var(--sunken,#eeece7)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={
                      replacePreviewComparison
                        ? openReplaceConfirmModal
                        : handleConfirmMerge
                    }
                    disabled={
                      isMerging ||
                      loading ||
                      (statusFilter !== "all" &&
                        (groupedInventory.length === 0 || totalRecords === 0))
                    }
                    className="inline-flex items-center justify-center gap-2 px-3 py-1 text-xs font-medium rounded text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--accent,#a51c1c)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isMerging || loading ? (
                      <>
                        <Spinner className="w-3.5 h-3.5 text-white" />
                        {replacePreviewComparison ? "Replacing…" : "Merging…"}
                      </>
                    ) : replacePreviewComparison ? (
                      "Replace inventory"
                    ) : (
                      "Confirm Merge"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {showReplaceConfirmModal && replacePreviewComparison && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
              onClick={closeReplaceConfirmModal}
            >
              <div
                className="bg-white rounded-[10px] shadow-xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="replace-confirm-title"
              >
                <div className="px-5 py-4 border-b border-[var(--line,#e4e2dc)]">
                  <h3
                    id="replace-confirm-title"
                    className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]"
                  >
                    Replace inventory?
                  </h3>
                  <p className="text-[13px] text-[var(--muted,#6b6a66)] mt-1">
                    The current {provider || "provider"}{" "}
                    {category || "category"} inventory set will be removed and
                    replaced with this file. A snapshot is kept in Import
                    history so you can roll back.
                  </p>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <ul className="text-[13px] text-[var(--ink,#1c1c1a)] space-y-1.5">
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.current_count ?? 0).toLocaleString()}
                      </span>{" "}
                      current records will be removed
                    </li>
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.file_count ?? 0).toLocaleString()}
                      </span>{" "}
                      records from this file will be imported
                    </li>
                    {/* <li>
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.counts?.leaving ?? 0).toLocaleString()}
                      </span>{" "}
                      leaving inventory
                    </li> */}
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.counts?.added ?? 0).toLocaleString()}
                      </span>{" "}
                      new ·{" "}
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.counts?.changed ?? 0).toLocaleString()}
                      </span>{" "}
                      changed ·{" "}
                      <span className="font-semibold tabular-nums">
                        {(replacePreviewComparison.counts?.unchanged ?? 0).toLocaleString()}
                      </span>{" "}
                      unchanged
                    </li>
                  </ul>
                  <label className="flex items-start gap-2 text-[13px] text-[var(--ink,#1c1c1a)]">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={replaceConfirmAcknowledged}
                      onChange={(e) =>
                        setReplaceConfirmAcknowledged(e.target.checked)
                      }
                      disabled={isMerging}
                    />
                    <span>
                      I understand the current {provider || "provider"}{" "}
                      {category || "category"} inventory will be removed and
                      replaced with this file.
                    </span>
                  </label>
                </div>
                <div className="px-5 py-4 border-t border-[var(--line,#e4e2dc)] flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeReplaceConfirmModal}
                    disabled={isMerging}
                    className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmMerge}
                    disabled={!replaceConfirmAcknowledged || isMerging || loading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isMerging || loading ? (
                      <>
                        <Spinner className="w-4 h-4 text-white" />
                        Replacing…
                      </>
                    ) : (
                      "Confirm replace"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <Loading
            show={isMerging}
            overlayMode="fixed"
            message={
              replacePreviewComparison
                ? "Replacing inventory…"
                : "Merging inventory…"
            }
            subMessage={
              replacePreviewComparison
                ? `Saving a snapshot, removing the current ${provider || ""} ${category || ""} set, and loading the new file.`
                : "Please wait while we merge your imported records."
            }
          />

          {editingPreviewItem && (
            <EditModal
              item={editingPreviewItem.new}
              originalItem={editingPreviewItem.original}
              onClose={() => setEditingPreviewItem(null)}
              onSave={handlePreviewSave}
              tenant={tenant}
              tenantId={tenantId}
              isNewRecord={editingPreviewItem.new.import_status === "new"}
              provider={category === "Custom" ? "Custom" : provider}
              category={category}
              providers={providers}
              categories={categories}
              serviceTypes={serviceTypes}
              fieldMapping={getFieldMappingFromAPI()}
              apiColumns={apiColumns}
              fieldMappingLookup={fieldMappingLookup}
              canMutate={canMutateTenantData(userRole)}
            />
          )}
        </div>
      </div>
    );
  }

  if (importStep === "complete") {
    return (
      <div className="min-h-screen pt-[155px] bg-brand-background">
        <div className="max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-3">
          <div
            className="flex items-center justify-center"
            style={{ minHeight: "calc(100vh - 400px)" }}
          >
            <div className="text-center bg-white p-12 rounded-md shadow-sm border-0">
              <div className="flex justify-center items-center gap-4 mb-4">
                <CheckCircleIcon className="w-16 h-16 text-green-500" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {replacePreviewComparison
                    ? "Replace Complete!"
                    : "Merge Complete!"}
                </h2>
              </div>
              <p className="text-gray-600 mb-4">
                Your inventory has been successfully updated.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={resetImport}
                  className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Import Another File
                </button>
                <button
                  onClick={() => onNavigate("inventory")}
                  className="px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] transition-colors"
                >
                  View Inventory
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ImportPage;
